(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-hz-hero-v2]';
  const instances = new WeakMap();

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const padIndex = (value) => String(value).padStart(2, '0');

  class HzHeroV2 {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('[data-hz-hero-viewport]');
      this.track = root.querySelector('[data-hz-hero-track]');
      this.slides = Array.from(root.querySelectorAll('[data-hz-hero-slide]'));
      this.prevButton = root.querySelector('[data-hz-hero-prev]');
      this.nextButton = root.querySelector('[data-hz-hero-next]');
      this.progress = root.querySelector('[data-hz-hero-progress]');
      this.progressActive = root.querySelector('[data-hz-hero-progress-active]');
      this.currentLabel = root.querySelector('[data-hz-hero-current]');
      this.totalLabel = root.querySelector('[data-hz-hero-total]');
      this.images = Array.from(root.querySelectorAll('[data-hz-hero-image]'));
      this.videos = Array.from(root.querySelectorAll('[data-hz-hero-video]'));
      this.videoToggles = Array.from(root.querySelectorAll('[data-hz-video-toggle]'));
      this.audioToggles = Array.from(root.querySelectorAll('[data-hz-video-audio-toggle]'));

      this.count = this.slides.length;
      this.currentIndex = 0;
      this.drag = null;
      this.suppressClick = false;
      this.userPausedVideos = new WeakSet();
      this.destroyed = false;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.mobileQuery = window.matchMedia('(max-width: 992px)');
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.videoAutoplay = root.dataset.videoAutoplay === 'true';
      this.videoLoop = root.dataset.videoLoop === 'true';
      this.videoPlayLabel = root.dataset.videoPlayLabel || 'Play video';
      this.videoPauseLabel = root.dataset.videoPauseLabel || 'Pause video';
      this.videoMuteLabel = root.dataset.videoMuteLabel || 'Mute video';
      this.videoUnmuteLabel = root.dataset.videoUnmuteLabel || 'Unmute video';
      this.posterPreloads = new WeakMap();

      if (!this.viewport || !this.track || this.count === 0) return;

      this.root.classList.add('hz__heroV2--ready');
      this.viewport.scrollLeft = 0;
      this.bindEvents();
      this.configureSlider();
      this.refreshAllMediaStatuses();
      this.prepareSlideMedia(0, 'high');
      this.prepareAdjacentMedia();
      this.setState(0, { animate: false });
      this.syncVideos();
    }

    bindEvents() {
      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => this.goTo(this.currentIndex - 1), { signal: this.signal });
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => this.goTo(this.currentIndex + 1), { signal: this.signal });
      }

      if (this.progress) {
        this.progress.addEventListener('click', (event) => this.onProgressClick(event), { signal: this.signal });
        this.progress.addEventListener('keydown', (event) => this.onProgressKeydown(event), { signal: this.signal });
      }

      this.viewport.addEventListener('keydown', (event) => this.onKeydown(event), { signal: this.signal });
      this.viewport.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal: this.signal });
      this.viewport.addEventListener('pointermove', (event) => this.onPointerMove(event), { signal: this.signal });
      this.viewport.addEventListener('pointerup', (event) => this.onPointerEnd(event), { signal: this.signal });
      this.viewport.addEventListener('pointercancel', (event) => this.onPointerCancel(event), { signal: this.signal });
      this.viewport.addEventListener('lostpointercapture', (event) => this.onLostPointerCapture(event), { signal: this.signal });

      this.root.addEventListener('click', (event) => this.onClickCapture(event), {
        capture: true,
        signal: this.signal
      });

      this.track.addEventListener('transitionend', (event) => {
        if (event.target === this.track && event.propertyName === 'transform') {
          this.onSettled();
        }
      }, { signal: this.signal });

      this.videoToggles.forEach((toggle) => {
        toggle.addEventListener('click', () => this.toggleActiveVideo(toggle), { signal: this.signal });
      });

      this.audioToggles.forEach((toggle) => {
        toggle.addEventListener('click', () => this.toggleActiveVideoAudio(toggle), { signal: this.signal });
      });

      this.images.forEach((image) => {
        const slide = image.closest('[data-hz-hero-slide]');
        image.addEventListener('load', () => this.refreshSlideMediaStatus(slide), { signal: this.signal });
        image.addEventListener('error', () => {
          image.dataset.hzLoadFailed = 'true';
          this.refreshSlideMediaStatus(slide);
        }, { signal: this.signal });
      });

      this.videos.forEach((video) => {
        video.loop = this.videoLoop;
        const slide = video.closest('[data-hz-hero-slide]');
        video.addEventListener('play', () => this.updateVideoToggleFor(video), { signal: this.signal });
        video.addEventListener('pause', () => this.updateVideoToggleFor(video), { signal: this.signal });
        video.addEventListener('ended', () => this.updateVideoToggleFor(video), { signal: this.signal });
        video.addEventListener('volumechange', () => this.updateAudioToggleFor(video), { signal: this.signal });
        video.addEventListener('loadeddata', () => this.refreshSlideMediaStatus(slide), { signal: this.signal });
        video.addEventListener('canplay', () => this.refreshSlideMediaStatus(slide), { signal: this.signal });
        video.addEventListener('error', () => {
          video.dataset.hzLoadFailed = 'true';
          this.refreshSlideMediaStatus(slide);
        }, { signal: this.signal });
      });

      this.mobileQuery.addEventListener('change', () => this.onMediaChange(), { signal: this.signal });
      this.reducedMotionQuery.addEventListener('change', () => this.syncVideos(), { signal: this.signal });
      document.addEventListener('visibilitychange', () => this.onVisibilityChange(), { signal: this.signal });
      window.addEventListener('pagehide', () => this.pauseAllVideos(), { signal: this.signal });
    }

    configureSlider() {
      this.root.style.setProperty('--hz__heroSlideCount', String(Math.max(this.count, 1)));
      this.root.style.setProperty('--hz__heroTrackOffset', '0%');
      this.root.style.setProperty('--hz__heroProgressOffset', '0%');
      this.root.style.setProperty('--hz__heroProgressWidth', `${100 / Math.max(this.count, 1)}%`);

      if (this.progressActive) {
        this.progressActive.style.width = `${100 / Math.max(this.count, 1)}%`;
      }

      if (this.totalLabel) {
        this.totalLabel.textContent = padIndex(this.count);
      }

      this.updateProgressAccessibility(this.currentIndex);
    }

    updateProgressAccessibility(index) {
      if (!this.progress || this.count < 2) return;

      this.progress.setAttribute(
        'aria-label',
        `Slide ${index + 1} of ${this.count}. Click a position on the progress line or use the arrow keys to navigate.`
      );
    }

    onProgressClick(event) {
      if (this.destroyed || this.count < 2 || !this.progress) return;

      // Keyboard-generated button clicks have no meaningful pointer coordinate;
      // keyboard navigation is handled explicitly in onProgressKeydown instead.
      if (event.detail === 0) return;

      const rect = this.progress.getBoundingClientRect();
      if (!rect.width) return;

      const position = clamp(event.clientX - rect.left, 0, rect.width);
      const ratio = position / rect.width;
      const targetIndex = clamp(Math.floor(ratio * this.count), 0, this.count - 1);
      this.goTo(targetIndex);
    }

    onProgressKeydown(event) {
      if (this.count < 2) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.currentIndex - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.currentIndex + 1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.goTo(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        this.goTo(this.count - 1);
      }
    }

    setState(index, { animate = true } = {}) {
      const nextIndex = clamp(index, 0, this.count - 1);
      this.currentIndex = nextIndex;

      if (!animate) {
        this.track.style.transition = 'none';
        if (this.progressActive) this.progressActive.style.transition = 'none';
      }

      this.root.style.setProperty('--hz__heroTrackOffset', `${nextIndex * -100}%`);
      this.root.style.setProperty('--hz__heroProgressOffset', `${nextIndex * 100}%`);
      this.track.style.removeProperty('transform');
      if (this.progressActive) this.progressActive.style.removeProperty('transform');

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === nextIndex;
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');

        if (isActive) {
          slide.removeAttribute('inert');
        } else {
          slide.setAttribute('inert', '');
        }
      });

      if (this.prevButton) this.prevButton.disabled = nextIndex === 0;
      if (this.nextButton) this.nextButton.disabled = nextIndex === this.count - 1;
      if (this.currentLabel) this.currentLabel.textContent = padIndex(nextIndex + 1);
      this.updateProgressAccessibility(nextIndex);

      this.syncVideos();

      if (!animate) {
        // Force the non-animated state to apply before restoring authored transitions.
        void this.track.offsetWidth;
        this.track.style.removeProperty('transition');
        if (this.progressActive) this.progressActive.style.removeProperty('transition');
      }
    }

    goTo(index, options = {}) {
      if (this.destroyed || this.count < 2) return;

      const nextIndex = clamp(index, 0, this.count - 1);
      if (nextIndex === this.currentIndex && !this.drag) return;

      this.prepareSlideMedia(nextIndex, 'high');
      this.setState(nextIndex, options);
      this.prepareAdjacentMedia();
    }

    onKeydown(event) {
      if (event.target !== this.viewport || this.count < 2) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.currentIndex - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.currentIndex + 1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.goTo(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        this.goTo(this.count - 1);
      }
    }

    onPointerDown(event) {
      if (this.count < 2 || !event.isPrimary || event.button !== 0) return;
      if (event.target.closest('a, button, input, select, textarea, summary, [contenteditable="true"]')) return;

      const width = this.viewport.clientWidth;
      if (!width) return;

      const startTranslate = this.getRenderedTranslateX();
      const baseFractionalIndex = clamp(-startTranslate / width, 0, this.count - 1);

      this.drag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        lastX: event.clientX,
        lastTime: performance.now(),
        velocityX: 0,
        startTranslate,
        currentTranslate: startTranslate,
        baseFractionalIndex,
        width,
        axis: null,
        moved: false
      };

      this.root.classList.add('hz__heroV2--dragging');
      this.track.style.transform = `translate3d(${startTranslate}px, 0, 0)`;

      if (this.progressActive) {
        this.progressActive.style.transform = `translate3d(${baseFractionalIndex * 100}%, -50%, 0)`;
      }

      try {
        this.viewport.setPointerCapture(event.pointerId);
      } catch (_error) {
        // Pointer capture can fail if the pointer is already no longer active.
      }
    }

    onPointerMove(event) {
      const drag = this.drag;
      if (!drag || event.pointerId !== drag.pointerId) return;

      const rawDx = event.clientX - drag.startX;
      const rawDy = event.clientY - drag.startY;

      if (!drag.axis) {
        if (Math.abs(rawDx) < 5 && Math.abs(rawDy) < 5) return;

        if (Math.abs(rawDy) > Math.abs(rawDx)) {
          drag.axis = 'vertical';
          this.releasePointerCapture(drag.pointerId);
          this.finishDragWithoutSwipe();
          return;
        }

        drag.axis = 'horizontal';
      }

      if (drag.axis !== 'horizontal') return;

      if (event.cancelable) event.preventDefault();

      const now = performance.now();
      const timeDelta = Math.max(now - drag.lastTime, 1);
      drag.velocityX = (event.clientX - drag.lastX) / timeDelta;
      drag.lastX = event.clientX;
      drag.lastTime = now;

      let nextTranslate = drag.startTranslate + rawDx;
      const minTranslate = -(this.count - 1) * drag.width;
      const maxTranslate = 0;

      if (nextTranslate > maxTranslate) {
        nextTranslate = maxTranslate + (nextTranslate - maxTranslate) * 0.22;
      } else if (nextTranslate < minTranslate) {
        nextTranslate = minTranslate + (nextTranslate - minTranslate) * 0.22;
      }

      drag.currentTranslate = nextTranslate;
      drag.moved = drag.moved || Math.abs(rawDx) >= 8;

      this.track.style.transform = `translate3d(${nextTranslate}px, 0, 0)`;

      if (this.progressActive) {
        const fractionalIndex = clamp(-nextTranslate / drag.width, 0, this.count - 1);
        this.progressActive.style.transform = `translate3d(${fractionalIndex * 100}%, -50%, 0)`;
      }
    }

    onPointerEnd(event) {
      const drag = this.drag;
      if (!drag || event.pointerId !== drag.pointerId) return;

      this.releasePointerCapture(drag.pointerId);

      if (drag.axis !== 'horizontal') {
        this.finishDragWithoutSwipe();
        return;
      }

      const rawIndex = clamp(-drag.currentTranslate / drag.width, 0, this.count - 1);
      const distance = event.clientX - drag.startX;
      const threshold = Math.min(Math.max(drag.width * 0.08, 24), 96);
      const fastSwipe = Math.abs(drag.velocityX) >= 0.35;
      let targetIndex = Math.round(rawIndex);

      if (fastSwipe || Math.abs(distance) >= threshold) {
        if (distance < 0 || drag.velocityX < -0.35) {
          targetIndex = Math.ceil(rawIndex - 0.001);
        } else if (distance > 0 || drag.velocityX > 0.35) {
          targetIndex = Math.floor(rawIndex + 0.001);
        }
      }

      targetIndex = clamp(targetIndex, 0, this.count - 1);
      this.prepareSlideMedia(targetIndex, 'high');

      if (drag.moved) {
        this.suppressClick = true;
        window.setTimeout(() => {
          this.suppressClick = false;
        }, 0);
      }

      // Keep the exact dragged transform until transitions are restored, then
      // remove the inline transform so CSS settles to the target slide smoothly.
      this.currentIndex = targetIndex;
      this.root.style.setProperty('--hz__heroTrackOffset', `${targetIndex * -100}%`);
      this.root.style.setProperty('--hz__heroProgressOffset', `${targetIndex * 100}%`);
      this.updateStateOnly(targetIndex);
      this.drag = null;
      this.root.classList.remove('hz__heroV2--dragging');

      requestAnimationFrame(() => {
        if (this.destroyed) return;
        this.track.style.removeProperty('transform');
        if (this.progressActive) this.progressActive.style.removeProperty('transform');
      });

      this.syncVideos();
      this.prepareAdjacentMedia();
    }

    onPointerCancel(event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;
      this.finishDragWithoutSwipe();
    }

    onLostPointerCapture(event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;
      if (this.drag.axis === 'horizontal') return;
      this.finishDragWithoutSwipe();
    }

    finishDragWithoutSwipe() {
      if (!this.drag) return;

      this.drag = null;
      this.root.classList.remove('hz__heroV2--dragging');

      requestAnimationFrame(() => {
        if (this.destroyed) return;
        this.track.style.removeProperty('transform');
        if (this.progressActive) this.progressActive.style.removeProperty('transform');
      });
    }

    releasePointerCapture(pointerId) {
      try {
        if (this.viewport.hasPointerCapture(pointerId)) {
          this.viewport.releasePointerCapture(pointerId);
        }
      } catch (_error) {
        // Safe no-op when capture has already been released by the browser.
      }
    }

    getRenderedTranslateX() {
      const transform = window.getComputedStyle(this.track).transform;
      if (!transform || transform === 'none') return -this.currentIndex * this.viewport.clientWidth;

      try {
        return new DOMMatrixReadOnly(transform).m41;
      } catch (_error) {
        const match = transform.match(/^matrix\(([^)]+)\)$/);
        if (!match) return -this.currentIndex * this.viewport.clientWidth;
        const values = match[1].split(',').map(Number);
        return Number.isFinite(values[4]) ? values[4] : -this.currentIndex * this.viewport.clientWidth;
      }
    }

    updateStateOnly(index) {
      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === index;
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        if (isActive) slide.removeAttribute('inert');
        else slide.setAttribute('inert', '');
      });

      if (this.prevButton) this.prevButton.disabled = index === 0;
      if (this.nextButton) this.nextButton.disabled = index === this.count - 1;
      if (this.currentLabel) this.currentLabel.textContent = padIndex(index + 1);
      this.updateProgressAccessibility(index);
    }

    onClickCapture(event) {
      if (!this.suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
      this.suppressClick = false;
    }

    onSettled() {
      this.prepareSlideMedia(this.currentIndex, 'high');
      this.prepareAdjacentMedia();
    }

    getActiveSlide() {
      return this.slides[this.currentIndex] || null;
    }

    getVisibleVideo(slide = this.getActiveSlide()) {
      if (!slide) return null;

      const videos = Array.from(slide.querySelectorAll('[data-hz-hero-video]'));
      if (videos.length === 0) return null;

      return videos.find((video) => {
        const holder = video.parentElement;
        if (!holder) return false;
        return window.getComputedStyle(holder).display !== 'none';
      }) || null;
    }

    getVisibleImage(slide = this.getActiveSlide()) {
      if (!slide) return null;

      const images = Array.from(slide.querySelectorAll('[data-hz-hero-image]'));
      if (images.length === 0) return null;

      return images.find((image) => {
        const holder = image.parentElement;
        if (!holder) return false;
        return window.getComputedStyle(holder).display !== 'none';
      }) || null;
    }

    getVideoToggle(slide = this.getActiveSlide()) {
      return slide ? slide.querySelector('[data-hz-video-toggle]') : null;
    }

    getAudioToggle(slide = this.getActiveSlide()) {
      return slide ? slide.querySelector('[data-hz-video-audio-toggle]') : null;
    }

    isSlideMediaReady(slide) {
      if (!slide) return true;

      const video = this.getVisibleVideo(slide);
      if (video) {
        if (video.dataset.hzLoadFailed === 'true') return true;
        if (video.readyState >= 2) return true;
        if (video.poster && video.dataset.hzPosterReady === 'true') return true;
        return false;
      }

      const image = this.getVisibleImage(slide);
      if (image) {
        if (image.dataset.hzLoadFailed === 'true') return true;
        return image.complete && image.naturalWidth > 0;
      }

      return true;
    }

    refreshSlideMediaStatus(slide) {
      if (!slide) return;
      slide.dataset.hzMediaStatus = this.isSlideMediaReady(slide) ? 'ready' : 'loading';
    }

    refreshAllMediaStatuses() {
      this.slides.forEach((slide) => this.refreshSlideMediaStatus(slide));
    }

    preloadVideoPoster(video) {
      if (!video || !video.poster || this.posterPreloads.has(video)) return;

      const poster = new Image();
      this.posterPreloads.set(video, poster);

      poster.onload = () => {
        if (this.destroyed) return;
        video.dataset.hzPosterReady = 'true';
        this.refreshSlideMediaStatus(video.closest('[data-hz-hero-slide]'));
      };

      poster.onerror = () => {
        if (this.destroyed) return;
        video.dataset.hzPosterFailed = 'true';
        this.refreshSlideMediaStatus(video.closest('[data-hz-hero-slide]'));
      };

      poster.src = video.poster;
    }

    prepareVideo(video, preload = 'auto') {
      if (!video) return;

      this.preloadVideoPoster(video);

      if (video.preload !== preload) {
        video.preload = preload;
      }

      if (video.readyState === 0 && preload === 'auto') {
        try {
          video.load();
        } catch (_error) {
          // The browser may reject an explicit load during teardown/navigation.
        }
      }
    }

    prepareSlideMedia(index, priority = 'adjacent') {
      if (index < 0 || index >= this.count) return;

      const slide = this.slides[index];
      const video = this.getVisibleVideo(slide);

      if (video) {
        this.prepareVideo(video, priority === 'high' ? 'auto' : 'metadata');
        this.refreshSlideMediaStatus(slide);
        return;
      }

      const image = this.getVisibleImage(slide);
      if (image) {
        if (!image.complete || image.naturalWidth === 0) {
          image.loading = 'eager';
          image.fetchPriority = priority === 'high' ? 'high' : 'auto';
        }
        this.refreshSlideMediaStatus(slide);
      }
    }

    prepareAdjacentMedia() {
      if (this.count < 2) return;

      [this.currentIndex - 1, this.currentIndex + 1].forEach((index) => {
        this.prepareSlideMedia(index, 'adjacent');
      });
    }

    syncVideos() {
      if (this.destroyed) return;

      const activeVideo = this.getVisibleVideo();
      const activeToggle = this.getVideoToggle();
      const activeAudioToggle = this.getAudioToggle();

      this.videoToggles.forEach((toggle) => {
        toggle.hidden = toggle !== activeToggle || !activeVideo;
      });

      this.audioToggles.forEach((toggle) => {
        toggle.hidden = toggle !== activeAudioToggle || !activeVideo;
      });

      this.videos.forEach((video) => {
        video.loop = this.videoLoop;

        if (video !== activeVideo) {
          video.muted = true;
          if (!video.paused) video.pause();
        }
      });

      if (!activeVideo) return;

      if (activeToggle) {
        activeToggle.hidden = false;
        this.updateVideoToggleFor(activeVideo);
      }

      if (activeAudioToggle) {
        activeAudioToggle.hidden = false;
        this.updateAudioToggleFor(activeVideo);
      }

      const canAutoplay =
        this.videoAutoplay &&
        !this.reducedMotionQuery.matches &&
        !document.hidden &&
        !this.userPausedVideos.has(activeVideo);

      if (!canAutoplay) {
        if (!activeVideo.paused && (this.reducedMotionQuery.matches || document.hidden)) {
          activeVideo.pause();
        }
        this.updateVideoToggleFor(activeVideo);
        this.updateAudioToggleFor(activeVideo);
        return;
      }

      this.prepareVideo(activeVideo, 'auto');
      const playPromise = activeVideo.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          this.updateVideoToggleFor(activeVideo);
          this.updateAudioToggleFor(activeVideo);
        });
      }
    }

    updateVideoToggleFor(video) {
      const activeVideo = this.getVisibleVideo();
      if (!video || video !== activeVideo) return;

      const toggle = this.getVideoToggle();
      if (!toggle) return;

      const isPlaying = !video.paused && !video.ended;
      toggle.dataset.state = isPlaying ? 'playing' : 'paused';
      toggle.setAttribute('aria-label', isPlaying ? this.videoPauseLabel : this.videoPlayLabel);
    }

    updateAudioToggleFor(video) {
      const activeVideo = this.getVisibleVideo();
      if (!video || video !== activeVideo) return;

      const toggle = this.getAudioToggle();
      if (!toggle) return;

      const isMuted = video.muted || video.volume === 0;
      toggle.dataset.state = isMuted ? 'muted' : 'unmuted';
      toggle.setAttribute('aria-label', isMuted ? this.videoUnmuteLabel : this.videoMuteLabel);
    }

    toggleActiveVideo(toggle) {
      const activeVideo = this.getVisibleVideo();
      if (!activeVideo || toggle !== this.getVideoToggle()) return;

      if (!activeVideo.paused && !activeVideo.ended) {
        this.userPausedVideos.add(activeVideo);
        activeVideo.pause();
        return;
      }

      this.userPausedVideos.delete(activeVideo);
      this.prepareVideo(activeVideo, 'auto');

      const playPromise = activeVideo.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => this.updateVideoToggleFor(activeVideo));
      }
    }

    toggleActiveVideoAudio(toggle) {
      const activeVideo = this.getVisibleVideo();
      if (!activeVideo || toggle !== this.getAudioToggle()) return;

      activeVideo.muted = !activeVideo.muted;
      this.updateAudioToggleFor(activeVideo);
    }

    pauseAllVideos() {
      this.videos.forEach((video) => {
        if (!video.paused) video.pause();
      });
    }

    onVisibilityChange() {
      if (document.hidden) this.pauseAllVideos();
      else this.syncVideos();
    }

    onMediaChange() {
      this.pauseAllVideos();
      this.refreshAllMediaStatuses();
      this.prepareSlideMedia(this.currentIndex, 'high');
      this.prepareAdjacentMedia();
      this.syncVideos();
    }

    selectBlock(blockId) {
      const index = this.slides.findIndex((slide) => slide.dataset.blockId === blockId);
      if (index >= 0) this.goTo(index);
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      this.pauseAllVideos();
      this.videos.forEach((video) => {
        const poster = this.posterPreloads.get(video);
        if (poster) {
          poster.onload = null;
          poster.onerror = null;
        }
      });
      this.abortController.abort();
      this.root.classList.remove('hz__heroV2--dragging', 'hz__heroV2--ready');
      this.slides.forEach((slide) => {
        slide.setAttribute('aria-hidden', 'false');
        slide.removeAttribute('inert');
      });
      this.track.style.removeProperty('transform');
      if (this.progressActive) this.progressActive.style.removeProperty('transform');
      instances.delete(this.root);
    }
  }

  function initRoot(root) {
    if (!root || instances.has(root)) return instances.get(root) || null;
    const instance = new HzHeroV2(root);
    instances.set(root, instance);
    return instance;
  }

  function initWithin(scope = document) {
    if (scope.matches && scope.matches(ROOT_SELECTOR)) initRoot(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initRoot);
  }

  function destroyWithin(scope) {
    if (!scope) return;

    const roots = [];
    if (scope.matches && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));

    roots.forEach((root) => {
      const instance = instances.get(root);
      if (instance) instance.destroy();
    });
  }

  function boot() {
    initWithin(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => initWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const blockId = event.detail?.blockId || event.target?.dataset?.blockId;
    if (!blockId) return;

    let root = event.target?.closest?.(ROOT_SELECTOR) || null;

    if (!root) {
      const block = Array.from(document.querySelectorAll('[data-hz-hero-slide]'))
        .find((slide) => slide.dataset.blockId === blockId);
      root = block?.closest(ROOT_SELECTOR) || null;
    }

    if (!root) return;

    const instance = instances.get(root) || initRoot(root);
    if (instance) instance.selectBlock(blockId);
  });
})();

(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-hz-collection-cards-v2]';
  const instances = new WeakMap();

  class HzCollectionCardsV2 {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('[data-hz-collection-viewport]');
      this.track = root.querySelector('[data-hz-collection-track]');
      this.realCards = this.track
        ? Array.from(this.track.querySelectorAll(':scope > [data-hz-collection-card]'))
        : [];
      this.mobileSliderEnabled = root.dataset.mobileSlider === 'true';
      this.sliderLabel = root.dataset.sliderLabel || 'Collection cards';
      this.mobileQuery = window.matchMedia('(max-width: 599.98px)');
      this.peekQuery = window.matchMedia('(max-width: 499.98px)');
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.legacyMediaCleanups = [];
      this.resizeObserver = null;
      this.active = false;
      this.destroyed = false;
      this.currentIndex = 0;
      this.positionIndex = 1;
      this.cardWidth = 0;
      this.gap = 0;
      this.centerOffset = 0;
      this.currentOffset = 0;
      this.drag = null;
      this.suppressClick = false;
      this.suppressClickTimer = 0;
      this.settleTimer = 0;
      this.transitioning = false;
      this.clones = [];

      if (!this.viewport || !this.track || this.realCards.length === 0) return;

      this.bindEvents();
      this.syncMode();
    }

    listenToMediaQuery(query, handler) {
      if (typeof query.addEventListener === 'function') {
        query.addEventListener('change', handler, { signal: this.signal });
        return;
      }

      if (typeof query.addListener === 'function') {
        query.addListener(handler);
        this.legacyMediaCleanups.push(() => query.removeListener(handler));
      }
    }

    bindEvents() {
      this.listenToMediaQuery(this.mobileQuery, () => this.syncMode());
      this.listenToMediaQuery(this.peekQuery, () => this.measureAndRender(false));
      this.listenToMediaQuery(this.reducedMotionQuery, () => this.measureAndRender(false));

      this.viewport.addEventListener('keydown', (event) => this.onKeydown(event), { signal: this.signal });
      this.viewport.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal: this.signal });
      this.viewport.addEventListener('pointermove', (event) => this.onPointerMove(event), { signal: this.signal });
      this.viewport.addEventListener('pointerup', (event) => this.onPointerEnd(event), { signal: this.signal });
      this.viewport.addEventListener('pointercancel', (event) => this.onPointerCancel(event), { signal: this.signal });
      this.viewport.addEventListener('lostpointercapture', (event) => this.onLostPointerCapture(event), { signal: this.signal });
      this.viewport.addEventListener('dragstart', (event) => {
        if (this.active) event.preventDefault();
      }, { signal: this.signal });

      this.root.addEventListener('click', (event) => {
        if (!this.active || !this.suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
        this.clearClickSuppression();
      }, { capture: true, signal: this.signal });

      this.root.addEventListener('focusin', (event) => this.onFocusIn(event), { signal: this.signal });

      this.track.addEventListener('transitionend', (event) => {
        if (!this.active || event.target !== this.track || event.propertyName !== 'transform') return;
        this.settleLoopPosition();
      }, { signal: this.signal });

      document.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event), { signal: this.signal });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          if (this.active) this.measureAndRender(false);
        });
        this.resizeObserver.observe(this.viewport);
      } else {
        window.addEventListener('resize', () => {
          if (this.active) this.measureAndRender(false);
        }, { passive: true, signal: this.signal });
      }
    }

    syncMode() {
      if (this.destroyed) return;

      const shouldActivate = this.mobileSliderEnabled && this.mobileQuery.matches && this.realCards.length > 1;
      if (shouldActivate) {
        this.activate();
      } else {
        this.deactivate();
      }
    }

    activate() {
      if (this.active || this.realCards.length < 2) return;

      this.active = true;
      this.root.classList.add('hz__collectionCardsV2--sliderReady');
      this.viewport.setAttribute('tabindex', '0');
      this.viewport.setAttribute('role', 'region');
      this.viewport.setAttribute('aria-roledescription', 'carousel');
      this.viewport.setAttribute('aria-label', this.sliderLabel);

      const lastClone = this.createClone(this.realCards[this.realCards.length - 1]);
      const firstClone = this.createClone(this.realCards[0]);
      this.clones = [lastClone, firstClone];
      this.track.insertBefore(lastClone, this.track.firstChild);
      this.track.appendChild(firstClone);

      this.currentIndex = Math.min(this.currentIndex, this.realCards.length - 1);
      this.positionIndex = this.currentIndex + 1;
      this.updateCurrentCard();
      this.prepareAround(this.currentIndex);

      requestAnimationFrame(() => this.measureAndRender(false));
    }

    deactivate() {
      window.clearTimeout(this.settleTimer);
      this.settleTimer = 0;
      this.clearClickSuppression();

      if (!this.active) {
        this.viewport?.removeAttribute('tabindex');
        this.viewport?.removeAttribute('role');
        this.viewport?.removeAttribute('aria-roledescription');
        this.viewport?.removeAttribute('aria-label');
        return;
      }

      this.drag = null;
      this.transitioning = false;
      this.root.classList.remove(
        'hz__collectionCardsV2--sliderReady',
        'hz__collectionCardsV2--dragging',
        'hz__collectionCardsV2--jumping'
      );
      this.viewport.removeAttribute('tabindex');
      this.viewport.removeAttribute('role');
      this.viewport.removeAttribute('aria-roledescription');
      this.viewport.removeAttribute('aria-label');
      this.root.style.removeProperty('--hz__collectionCardsSliderOffset');
      this.root.style.removeProperty('--hz__collectionCardsSliderCardWidth');

      this.clones.forEach((clone) => clone.remove());
      this.clones = [];
      this.realCards.forEach((card) => card.removeAttribute('aria-current'));
      this.active = false;
    }

    createClone(source) {
      const clone = source.cloneNode(true);
      clone.dataset.hzClone = 'true';
      clone.removeAttribute('data-hz-index');
      clone.removeAttribute('data-block-id');
      clone.removeAttribute('aria-current');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      clone.removeAttribute('data-shopify-editor-block');
      clone.querySelectorAll('[data-shopify-editor-block]').forEach((element) => {
        element.removeAttribute('data-shopify-editor-block');
      });
      clone.querySelectorAll('[data-block-id]').forEach((element) => element.removeAttribute('data-block-id'));
      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      clone.querySelectorAll('a, button, input, select, textarea, summary, [tabindex]').forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });
      if (clone.matches('a, button, input, select, textarea, summary, [tabindex]')) {
        clone.setAttribute('tabindex', '-1');
      }
      clone.querySelectorAll('img').forEach((image) => {
        image.loading = 'eager';
        image.fetchPriority = 'low';
      });
      return clone;
    }

    measureAndRender(animate = false) {
      if (!this.active || this.destroyed) return;

      const viewportWidth = this.viewport.clientWidth;
      if (!viewportWidth) return;

      const styles = window.getComputedStyle(this.track);
      this.gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;

      let peek = 0;
      if (this.peekQuery.matches) {
        const rootStyles = window.getComputedStyle(this.root);
        peek = Number.parseFloat(rootStyles.getPropertyValue('--hz__collectionCardsMobilePeek')) || 20;
      }

      this.cardWidth = Math.max(180, viewportWidth - (peek > 0 ? 2 * (peek + this.gap) : 0));
      this.centerOffset = Math.max(0, (viewportWidth - this.cardWidth) / 2);
      this.root.style.setProperty('--hz__collectionCardsSliderCardWidth', `${this.cardWidth}px`);
      this.renderPosition(animate);
    }

    offsetForPosition(positionIndex) {
      return this.centerOffset - positionIndex * (this.cardWidth + this.gap);
    }

    renderPosition(animate = true) {
      if (!this.active) return;

      const offset = this.offsetForPosition(this.positionIndex);
      this.currentOffset = offset;
      const instant = !animate || this.reducedMotionQuery.matches;

      if (instant) {
        this.root.classList.add('hz__collectionCardsV2--jumping');
        // Commit the no-transition state before changing transform so loop resets never animate backwards.
        void this.track.offsetWidth;
      }

      this.root.style.setProperty('--hz__collectionCardsSliderOffset', `${offset}px`);

      if (instant) {
        // Commit the destination while transitions are disabled, then restore animation next frame.
        void this.track.offsetWidth;
        requestAnimationFrame(() => {
          if (!this.destroyed) this.root.classList.remove('hz__collectionCardsV2--jumping');
        });
      }
    }

    go(direction) {
      if (!this.active || this.drag || this.transitioning || !direction) return;

      this.transitioning = !this.reducedMotionQuery.matches;
      this.positionIndex += direction;
      this.currentIndex = (this.currentIndex + direction + this.realCards.length) % this.realCards.length;
      this.updateCurrentCard();
      this.prepareAround(this.currentIndex);
      this.renderPosition(true);
      this.scheduleSettleFallback();
    }

    goToReal(index, animate = true) {
      if (!this.active) return;

      const nextIndex = Math.min(Math.max(index, 0), this.realCards.length - 1);
      if (nextIndex === this.currentIndex) {
        this.measureAndRender(false);
        return;
      }

      this.currentIndex = nextIndex;
      this.positionIndex = nextIndex + 1;
      this.updateCurrentCard();
      this.prepareAround(nextIndex);
      this.transitioning = Boolean(animate && !this.reducedMotionQuery.matches);
      this.renderPosition(animate);
      this.scheduleSettleFallback();
    }

    scheduleSettleFallback() {
      window.clearTimeout(this.settleTimer);
      this.settleTimer = window.setTimeout(
        () => this.settleLoopPosition(),
        this.reducedMotionQuery.matches ? 40 : 620
      );
    }

    settleLoopPosition() {
      if (!this.active) return;

      window.clearTimeout(this.settleTimer);
      this.settleTimer = 0;
      this.transitioning = false;

      if (this.positionIndex === 0) {
        this.positionIndex = this.realCards.length;
        this.renderPosition(false);
      } else if (this.positionIndex === this.realCards.length + 1) {
        this.positionIndex = 1;
        this.renderPosition(false);
      }
    }

    updateCurrentCard() {
      this.realCards.forEach((card, index) => {
        if (index === this.currentIndex) {
          card.setAttribute('aria-current', 'true');
        } else {
          card.removeAttribute('aria-current');
        }
      });
    }

    prepareAround(index) {
      const count = this.realCards.length;
      [index, (index - 1 + count) % count, (index + 1) % count].forEach((cardIndex, order) => {
        const card = this.realCards[cardIndex];
        if (!card) return;

        card.querySelectorAll('img').forEach((image) => {
          if (image.loading === 'lazy') image.loading = 'eager';
          image.fetchPriority = order === 0 ? 'high' : 'low';
          if (typeof image.decode === 'function') image.decode().catch(() => {});
        });
      });
    }

    onKeydown(event) {
      if (!this.active || event.target !== this.viewport) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.go(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.go(1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.goToReal(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        this.goToReal(this.realCards.length - 1);
      }
    }

    onPointerDown(event) {
      if (!this.active || !event.isPrimary || event.button !== 0 || this.transitioning) return;

      this.drag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        lastX: event.clientX,
        startTime: performance.now(),
        moved: false
      };
      this.clearClickSuppression();
      this.root.classList.add('hz__collectionCardsV2--dragging');

      try {
        this.viewport.setPointerCapture(event.pointerId);
      } catch (_) {
        // Pointer capture is an enhancement; dragging remains usable without it.
      }
    }

    onPointerMove(event) {
      if (!this.active || !this.drag || event.pointerId !== this.drag.pointerId) return;

      const deltaX = event.clientX - this.drag.startX;
      this.drag.lastX = event.clientX;
      if (Math.abs(deltaX) > 5) this.drag.moved = true;
      if (this.drag.moved) event.preventDefault();

      const offset = this.offsetForPosition(this.positionIndex) + deltaX;
      this.currentOffset = offset;
      this.root.style.setProperty('--hz__collectionCardsSliderOffset', `${offset}px`);
    }

    onPointerEnd(event) {
      if (!this.active || !this.drag || event.pointerId !== this.drag.pointerId) return;

      const drag = this.drag;
      this.drag = null;
      this.root.classList.remove('hz__collectionCardsV2--dragging');

      const deltaX = event.clientX - drag.startX;
      const elapsed = Math.max(performance.now() - drag.startTime, 1);
      const velocity = deltaX / elapsed;
      const threshold = Math.min(72, this.cardWidth * 0.18);
      const shouldAdvance = Math.abs(deltaX) >= threshold || Math.abs(velocity) >= 0.5;

      if (drag.moved) this.startClickSuppression();

      if (shouldAdvance) {
        this.go(deltaX < 0 ? 1 : -1);
      } else {
        this.transitioning = !this.reducedMotionQuery.matches;
        this.renderPosition(true);
        this.scheduleSettleFallback();
      }
    }

    onPointerCancel(event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;

      this.drag = null;
      this.root.classList.remove('hz__collectionCardsV2--dragging');
      this.transitioning = !this.reducedMotionQuery.matches;
      this.renderPosition(true);
      this.scheduleSettleFallback();
    }

    onLostPointerCapture(event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;
      this.onPointerCancel(event);
    }

    startClickSuppression() {
      window.clearTimeout(this.suppressClickTimer);
      this.suppressClick = true;
      this.suppressClickTimer = window.setTimeout(() => {
        this.suppressClick = false;
        this.suppressClickTimer = 0;
      }, 200);
    }

    clearClickSuppression() {
      window.clearTimeout(this.suppressClickTimer);
      this.suppressClickTimer = 0;
      this.suppressClick = false;
    }

    onFocusIn(event) {
      if (!this.active) return;

      const card = event.target.closest?.('[data-hz-collection-card]:not([data-hz-clone])');
      if (!card || !this.root.contains(card)) return;

      const index = Number.parseInt(card.dataset.hzIndex, 10);
      if (Number.isInteger(index) && index !== this.currentIndex) this.goToReal(index);
    }

    onBlockSelect(event) {
      if (!this.active || !this.root.isConnected) return;

      const blockId = event.detail?.blockId || event.target?.dataset?.blockId;
      if (!blockId) return;

      let card = event.target?.closest?.('[data-hz-collection-card]:not([data-hz-clone])');
      if (!card || !this.root.contains(card)) {
        card = this.realCards.find((item) => item.dataset.blockId === blockId) || null;
      }

      if (!card) return;
      const index = Number.parseInt(card.dataset.hzIndex, 10);
      if (Number.isInteger(index)) this.goToReal(index, false);
    }

    destroy() {
      if (this.destroyed) return;

      this.destroyed = true;
      this.deactivate();
      this.resizeObserver?.disconnect();
      this.legacyMediaCleanups.forEach((cleanup) => cleanup());
      this.legacyMediaCleanups = [];
      this.abortController.abort();
    }
  }

  const init = (scope = document) => {
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    roots.push(...(scope.querySelectorAll?.(ROOT_SELECTOR) || []));

    roots.forEach((root) => {
      if (instances.has(root)) return;
      const instance = new HzCollectionCardsV2(root);
      instances.set(root, instance);
    });
  };

  const destroy = (scope) => {
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    roots.push(...(scope.querySelectorAll?.(ROOT_SELECTOR) || []));

    roots.forEach((root) => {
      const instance = instances.get(root);
      if (!instance) return;
      instance.destroy();
      instances.delete(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();

/* ========================================================================== 
   AR Product Cards V2
   Infinite product carousel plus a narrow bridge into Ella's authoritative
   add-to-cart/cart-drawer pipeline.
   ========================================================================== */

(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-hz-product-cards-v2]';
  const instances = new WeakMap();

  class HzProductCardsV2 {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('[data-hz-product-cards-viewport]');
      this.track = root.querySelector('[data-hz-product-cards-track]');
      this.nav = root.querySelector('[data-hz-product-cards-nav]');
      this.prevButton = root.querySelector('[data-hz-product-cards-prev]');
      this.nextButton = root.querySelector('[data-hz-product-cards-next]');
      this.realCards = this.track
        ? Array.from(this.track.querySelectorAll(':scope > [data-hz-product-card]'))
        : [];
      this.mobileQuery = window.matchMedia('(max-width: 599.98px)');
      this.tabletQuery = window.matchMedia('(min-width: 600px) and (max-width: 1023.98px)');
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.legacyMediaCleanups = [];
      this.resizeObserver = null;
      this.intersectionObserver = null;
      this.active = false;
      this.destroyed = false;
      this.nearViewport = false;
      this.currentIndex = 0;
      this.positionIndex = 0;
      this.cardWidth = 0;
      this.gap = 0;
      this.centerOffset = 0;
      this.drag = null;
      this.transitioning = false;
      this.suppressClick = false;
      this.suppressClickTimer = 0;
      this.settleTimer = 0;
      this.clones = [];
      this.bufferCount = 0;

      if (!this.viewport || !this.track || this.realCards.length === 0) return;

      this.realCards.forEach((card, index) => {
        card.dataset.hzIndex = String(index);
      });

      this.bindEvents();
      this.activate();
    }

    listenToMediaQuery(query, handler) {
      if (typeof query.addEventListener === 'function') {
        query.addEventListener('change', handler, { signal: this.signal });
        return;
      }

      if (typeof query.addListener === 'function') {
        query.addListener(handler);
        this.legacyMediaCleanups.push(() => query.removeListener(handler));
      }
    }

    bindEvents() {
      const remeasure = () => this.measureAndRender(false);
      this.listenToMediaQuery(this.mobileQuery, remeasure);
      this.listenToMediaQuery(this.tabletQuery, remeasure);
      this.listenToMediaQuery(this.reducedMotionQuery, remeasure);

      this.prevButton?.addEventListener('click', () => this.go(-1), { signal: this.signal });
      this.nextButton?.addEventListener('click', () => this.go(1), { signal: this.signal });
      this.viewport.addEventListener('keydown', (event) => this.onKeydown(event), { signal: this.signal });
      this.viewport.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal: this.signal });
      this.viewport.addEventListener('pointermove', (event) => this.onPointerMove(event), { signal: this.signal });
      this.viewport.addEventListener('pointerup', (event) => this.onPointerEnd(event), { signal: this.signal });
      this.viewport.addEventListener('pointercancel', (event) => this.onPointerCancel(event), { signal: this.signal });
      this.viewport.addEventListener('lostpointercapture', (event) => this.onLostPointerCapture(event), { signal: this.signal });
      this.viewport.addEventListener('dragstart', (event) => {
        if (this.active) event.preventDefault();
      }, { signal: this.signal });

      this.root.addEventListener('click', (event) => {
        if (!this.active || !this.suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
        this.clearClickSuppression();
      }, { capture: true, signal: this.signal });

      this.root.addEventListener('focusin', (event) => this.onFocusIn(event), { signal: this.signal });

      this.track.addEventListener('transitionend', (event) => {
        if (!this.active || event.target !== this.track || event.propertyName !== 'transform') return;
        this.settleLoopPosition();
      }, { signal: this.signal });

      document.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event), { signal: this.signal });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          if (this.active) this.measureAndRender(false);
        });
        this.resizeObserver.observe(this.viewport);
      } else {
        window.addEventListener('resize', () => {
          if (this.active) this.measureAndRender(false);
        }, { passive: true, signal: this.signal });
      }

      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver((entries) => {
          this.nearViewport = entries.some((entry) => entry.isIntersecting);
          if (this.nearViewport) this.prepareAround(this.currentIndex);
        }, { rootMargin: '320px 0px', threshold: 0 });
        this.intersectionObserver.observe(this.root);
      } else {
        this.nearViewport = true;
      }
    }

    activate() {
      if (this.active || this.realCards.length < 2) {
        this.updateNavState();
        return;
      }

      this.active = true;
      this.root.classList.add('hz__productCardsV2--sliderReady');
      this.nav && (this.nav.hidden = false);
      this.viewport.setAttribute('tabindex', '0');
      this.viewport.setAttribute('role', 'region');
      this.viewport.setAttribute('aria-roledescription', 'carousel');
      this.viewport.setAttribute('aria-label', this.root.dataset.sliderLabel || 'Products');

      const beforeFragment = document.createDocumentFragment();
      const afterFragment = document.createDocumentFragment();
      const rootStyles = window.getComputedStyle(this.root);
      const desktopVisible = Number.parseInt(rootStyles.getPropertyValue('--hz__productCardsColumnsDesktop'), 10) || 1;
      const tabletVisible = Number.parseInt(rootStyles.getPropertyValue('--hz__productCardsColumnsTablet'), 10) || 1;
      const maxVisible = Math.max(desktopVisible, tabletVisible, 1);
      this.bufferCount = Math.min(this.realCards.length, maxVisible + 1);

      this.realCards.slice(-this.bufferCount).forEach((card) => beforeFragment.appendChild(this.createClone(card)));
      this.realCards.slice(0, this.bufferCount).forEach((card) => afterFragment.appendChild(this.createClone(card)));
      this.track.insertBefore(beforeFragment, this.track.firstChild);
      this.track.appendChild(afterFragment);
      this.clones = Array.from(this.track.querySelectorAll(':scope > [data-hz-clone="true"]'));

      this.positionIndex = this.bufferCount + this.currentIndex;
      this.updateCurrentCard();
      this.updateNavState();
      requestAnimationFrame(() => this.measureAndRender(false));
    }

    createClone(source) {
      const clone = source.cloneNode(true);
      clone.dataset.hzClone = 'true';
      clone.removeAttribute('data-hz-index');
      clone.removeAttribute('data-block-id');
      clone.removeAttribute('data-shopify-editor-block');
      clone.removeAttribute('id');
      clone.removeAttribute('aria-current');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      clone.querySelectorAll('[data-block-id], [data-shopify-editor-block]').forEach((element) => {
        element.removeAttribute('data-block-id');
        element.removeAttribute('data-shopify-editor-block');
      });
      clone.querySelectorAll('a, button, input, select, textarea, summary, [tabindex]').forEach((element) => {
        element.setAttribute('tabindex', '-1');
        if ('disabled' in element && !element.matches('a')) element.disabled = true;
      });
      clone.querySelectorAll('img').forEach((image) => {
        image.loading = 'lazy';
        image.fetchPriority = 'low';
      });
      return clone;
    }

    getVisibleCount() {
      if (this.mobileQuery.matches) return 1;
      const styles = window.getComputedStyle(this.root);
      const property = this.tabletQuery.matches
        ? '--hz__productCardsColumnsTablet'
        : '--hz__productCardsColumnsDesktop';
      const parsed = Number.parseInt(styles.getPropertyValue(property), 10);
      return Math.min(Math.max(Number.isFinite(parsed) ? parsed : 1, 1), this.realCards.length);
    }

    measureAndRender(animate = false) {
      if (!this.active || this.destroyed) return;

      const viewportWidth = this.viewport.clientWidth;
      if (!viewportWidth) return;

      const trackStyles = window.getComputedStyle(this.track);
      this.gap = Number.parseFloat(trackStyles.columnGap || trackStyles.gap) || 0;

      if (this.mobileQuery.matches) {
        const rootStyles = window.getComputedStyle(this.root);
        const peekPercent = Number.parseFloat(rootStyles.getPropertyValue('--hz__productCardsMobilePeek')) || 10;
        const visiblePeek = viewportWidth * Math.min(Math.max(peekPercent, 0), 30) / 100;
        this.centerOffset = visiblePeek + this.gap;
        this.cardWidth = Math.max(180, viewportWidth - (2 * this.centerOffset));
      } else {
        const visibleCount = this.getVisibleCount();
        this.centerOffset = 0;
        this.cardWidth = Math.max(140, (viewportWidth - (this.gap * Math.max(visibleCount - 1, 0))) / visibleCount);
      }

      this.root.style.setProperty('--hz__productCardsSlideWidth', `${this.cardWidth}px`);
      this.renderPosition(animate);
      if (this.nearViewport) this.prepareAround(this.currentIndex);
    }

    offsetForPosition(positionIndex) {
      return this.centerOffset - (positionIndex * (this.cardWidth + this.gap));
    }

    renderPosition(animate = true) {
      if (!this.active) return;

      const instant = !animate || this.reducedMotionQuery.matches;
      if (instant) {
        this.root.classList.add('hz__productCardsV2--jumping');
        void this.track.offsetWidth;
      }

      const offset = this.offsetForPosition(this.positionIndex);
      this.root.style.setProperty('--hz__productCardsSliderOffset', `${offset}px`);

      if (instant) {
        void this.track.offsetWidth;
        requestAnimationFrame(() => {
          if (!this.destroyed) this.root.classList.remove('hz__productCardsV2--jumping');
        });
      }
    }

    go(direction) {
      if (!this.active || this.drag || this.transitioning || !direction) return;

      this.transitioning = !this.reducedMotionQuery.matches;
      this.positionIndex += direction;
      this.currentIndex = (this.currentIndex + direction + this.realCards.length) % this.realCards.length;
      this.updateCurrentCard();
      this.prepareAround(this.currentIndex);
      this.renderPosition(true);
      this.scheduleSettleFallback();
    }

    goToReal(index, animate = true) {
      if (!this.active) return;

      const nextIndex = Math.min(Math.max(index, 0), this.realCards.length - 1);
      this.currentIndex = nextIndex;
      this.positionIndex = this.bufferCount + nextIndex;
      this.updateCurrentCard();
      this.prepareAround(nextIndex);
      this.transitioning = Boolean(animate && !this.reducedMotionQuery.matches);
      this.renderPosition(animate);
      this.scheduleSettleFallback();
    }

    scheduleSettleFallback() {
      window.clearTimeout(this.settleTimer);
      this.settleTimer = window.setTimeout(
        () => this.settleLoopPosition(),
        this.reducedMotionQuery.matches ? 40 : 620
      );
    }

    settleLoopPosition() {
      if (!this.active) return;

      window.clearTimeout(this.settleTimer);
      this.settleTimer = 0;
      this.transitioning = false;

      const count = this.realCards.length;
      if (this.positionIndex < this.bufferCount) {
        this.positionIndex += count;
        this.renderPosition(false);
      } else if (this.positionIndex >= this.bufferCount + count) {
        this.positionIndex -= count;
        this.renderPosition(false);
      }
    }

    updateCurrentCard() {
      this.realCards.forEach((card, index) => {
        if (index === this.currentIndex) {
          card.setAttribute('aria-current', 'true');
        } else {
          card.removeAttribute('aria-current');
        }
      });
    }

    updateNavState() {
      const enabled = this.realCards.length > 1;
      [this.prevButton, this.nextButton].forEach((button) => {
        if (!button) return;
        button.disabled = !enabled;
        button.setAttribute('aria-disabled', String(!enabled));
      });
      if (this.nav && !enabled) this.nav.hidden = true;
    }

    prepareAround(index) {
      if (!this.nearViewport && this.intersectionObserver) return;

      const count = this.realCards.length;
      const visibleCount = this.getVisibleCount();
      const indexes = new Set();
      for (let offset = -1; offset <= visibleCount; offset += 1) {
        indexes.add((index + offset + count) % count);
      }

      indexes.forEach((cardIndex) => {
        const card = this.realCards[cardIndex];
        if (!card) return;
        card.querySelectorAll('img').forEach((image) => {
          if (image.loading === 'lazy') image.loading = 'eager';
          if (typeof image.decode === 'function') image.decode().catch(() => {});
        });
      });
    }

    isIndexVisible(index) {
      const visibleCount = this.getVisibleCount();
      for (let offset = 0; offset < visibleCount; offset += 1) {
        if ((this.currentIndex + offset) % this.realCards.length === index) return true;
      }
      return false;
    }

    onKeydown(event) {
      if (!this.active || event.target !== this.viewport) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.go(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.go(1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.goToReal(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        this.goToReal(this.realCards.length - 1);
      }
    }

    onPointerDown(event) {
      if (!this.active || !event.isPrimary || event.button !== 0 || this.transitioning) return;
      if (event.target.closest('button, input, select, textarea')) return;

      this.drag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        lastX: event.clientX,
        startTime: performance.now(),
        moved: false
      };
      this.clearClickSuppression();
      this.root.classList.add('hz__productCardsV2--dragging');

      try {
        this.viewport.setPointerCapture(event.pointerId);
      } catch (_) {
        // Pointer capture is an enhancement; dragging remains usable without it.
      }
    }

    onPointerMove(event) {
      if (!this.active || !this.drag || event.pointerId !== this.drag.pointerId) return;

      const deltaX = event.clientX - this.drag.startX;
      this.drag.lastX = event.clientX;
      if (Math.abs(deltaX) > 5) this.drag.moved = true;
      if (this.drag.moved) event.preventDefault();

      const offset = this.offsetForPosition(this.positionIndex) + deltaX;
      this.root.style.setProperty('--hz__productCardsSliderOffset', `${offset}px`);
    }

    onPointerEnd(event) {
      if (!this.active || !this.drag || event.pointerId !== this.drag.pointerId) return;

      const drag = this.drag;
      this.drag = null;
      this.root.classList.remove('hz__productCardsV2--dragging');

      const deltaX = event.clientX - drag.startX;
      const elapsed = Math.max(performance.now() - drag.startTime, 1);
      const velocity = deltaX / elapsed;
      const threshold = Math.min(84, this.cardWidth * 0.18);
      const shouldAdvance = Math.abs(deltaX) >= threshold || Math.abs(velocity) >= 0.5;

      if (drag.moved) this.startClickSuppression();

      if (shouldAdvance) {
        this.go(deltaX < 0 ? 1 : -1);
      } else {
        this.transitioning = !this.reducedMotionQuery.matches;
        this.renderPosition(true);
        this.scheduleSettleFallback();
      }
    }

    onPointerCancel(event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;
      this.drag = null;
      this.root.classList.remove('hz__productCardsV2--dragging');
      this.transitioning = !this.reducedMotionQuery.matches;
      this.renderPosition(true);
      this.scheduleSettleFallback();
    }

    onLostPointerCapture(event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;
      this.onPointerCancel(event);
    }

    startClickSuppression() {
      window.clearTimeout(this.suppressClickTimer);
      this.suppressClick = true;
      this.suppressClickTimer = window.setTimeout(() => this.clearClickSuppression(), 220);
    }

    clearClickSuppression() {
      window.clearTimeout(this.suppressClickTimer);
      this.suppressClickTimer = 0;
      this.suppressClick = false;
    }

    onFocusIn(event) {
      if (!this.active) return;
      const card = event.target.closest?.('[data-hz-product-card]:not([data-hz-clone])');
      if (!card || !this.root.contains(card)) return;
      const index = Number.parseInt(card.dataset.hzIndex, 10);
      if (Number.isInteger(index) && !this.isIndexVisible(index)) this.goToReal(index);
    }

    onBlockSelect(event) {
      if (!this.active || !this.root.contains(event.target)) return;
      const blockId = event.detail?.blockId;
      if (!blockId) return;
      const index = this.realCards.findIndex((card) => card.dataset.blockId === blockId);
      if (index >= 0) this.goToReal(index, !event.detail?.load);
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      window.clearTimeout(this.settleTimer);
      this.clearClickSuppression();
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      this.legacyMediaCleanups.forEach((cleanup) => cleanup());
      this.legacyMediaCleanups = [];
      this.abortController.abort();
      this.clones.forEach((clone) => clone.remove());
      this.clones = [];
      this.root.classList.remove('hz__productCardsV2--sliderReady', 'hz__productCardsV2--dragging', 'hz__productCardsV2--jumping');
      this.root.style.removeProperty('--hz__productCardsSlideWidth');
      this.root.style.removeProperty('--hz__productCardsSliderOffset');
      this.viewport?.removeAttribute('tabindex');
      this.viewport?.removeAttribute('role');
      this.viewport?.removeAttribute('aria-roledescription');
      this.viewport?.removeAttribute('aria-label');
      if (this.nav) this.nav.hidden = true;
    }
  }

  const init = (scope = document) => {
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    roots.push(...(scope.querySelectorAll?.(ROOT_SELECTOR) || []));

    roots.forEach((root) => {
      if (instances.has(root)) return;
      const instance = new HzProductCardsV2(root);
      instances.set(root, instance);
    });
  };

  const destroy = (scope) => {
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    roots.push(...(scope.querySelectorAll?.(ROOT_SELECTOR) || []));

    roots.forEach((root) => {
      const instance = instances.get(root);
      if (!instance) return;
      instance.destroy();
      instances.delete(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();

(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-hz-product-cards-v2]';
  const ATC_SELECTOR = '[data-hz-product-card-atc]';
  const FORCE_TIMEOUT = 15000;

  if (window.hz__productCardsV2DrawerBridgeBound) return;
  window.hz__productCardsV2DrawerBridgeBound = true;

  let activeForces = 0;
  let savedState = null;

  const saveAndForceDrawer = () => {
    if (!window.after_add_to_cart || !window.quick_cart) return false;

    if (!savedState) {
      savedState = {
        afterType: window.after_add_to_cart.type,
        quickShow: window.quick_cart.show,
        quickTypeHadOwnProperty: Object.prototype.hasOwnProperty.call(window.quick_cart, 'type'),
        quickType: window.quick_cart.type
      };
    }

    activeForces += 1;
    window.after_add_to_cart.type = 'quick_cart';
    window.quick_cart.show = true;
    /* Ella treats every non-'popup' quick-cart type as the sidebar drawer. */
    delete window.quick_cart.type;
    return true;
  };

  const restoreDrawerSettings = () => {
    activeForces = Math.max(0, activeForces - 1);
    if (activeForces !== 0 || !savedState) return;

    if (window.after_add_to_cart && window.after_add_to_cart.type === 'quick_cart') {
      window.after_add_to_cart.type = savedState.afterType;
    }

    if (window.quick_cart) {
      if (window.quick_cart.show === true) window.quick_cart.show = savedState.quickShow;

      if (!Object.prototype.hasOwnProperty.call(window.quick_cart, 'type') && savedState.quickTypeHadOwnProperty) {
        window.quick_cart.type = savedState.quickType;
      }
    }

    savedState = null;
  };

  const bridgeNativeAddToCart = (button) => {
    if (!saveAndForceDrawer()) return;

    let observedLoading = button.classList.contains('is-loading');
    let settled = false;
    let timeoutId = 0;

    const finish = () => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      window.clearTimeout(timeoutId);
      restoreDrawerSettings();
    };

    const observer = new MutationObserver(() => {
      if (button.classList.contains('is-loading')) {
        observedLoading = true;
        return;
      }
      if (observedLoading) window.requestAnimationFrame(finish);
    });

    observer.observe(button, { attributes: true, attributeFilter: ['class'] });

    window.requestAnimationFrame(() => {
      if (button.classList.contains('is-loading')) observedLoading = true;
    });

    timeoutId = window.setTimeout(finish, FORCE_TIMEOUT);
  };

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest?.(ATC_SELECTOR);
      if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
      if (!button.closest(ROOT_SELECTOR) || button.closest('[data-hz-clone="true"]')) return;

      if (button.classList.contains('is-loading')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      bridgeNativeAddToCart(button);
    },
    true
  );
})();

(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-hz-hybrid-gallery-v2]';
  const LANE_SELECTOR = '[data-hz-hybrid-lane]';
  const SLIDE_SELECTOR = '[data-hz-hybrid-slide]';
  const MOBILE_QUERY = '(max-width: 749.98px)';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  const WHEEL_THRESHOLD = 40;
  const WHEEL_COOLDOWN = 440;
  const DRAG_DISTANCE_RATIO = 0.16;
  const DRAG_VELOCITY = 0.5;
  const instances = new WeakMap();

  const mod = (value, length) => {
    if (!length) return 0;
    const result = value % length;
    return result < 0 ? result + length : result;
  };

  const toBoolean = (value, fallback = false) => {
    if (value === undefined || value === null || value === '') return fallback;
    return value === true || value === 'true';
  };

  const toNumber = (value, fallback = 0) => {
    const number = Number.parseFloat(value);
    return Number.isFinite(number) ? number : fallback;
  };

  const closestEquivalent = (logicalIndex, currentVirtual, count) => {
    if (count <= 1) return logicalIndex;
    const base = logicalIndex + Math.round((currentVirtual - logicalIndex) / count) * count;
    const alternatives = [base - count, base, base + count];
    return alternatives.reduce((best, candidate) => {
      const bestDistance = Math.abs(best - currentVirtual);
      const candidateDistance = Math.abs(candidate - currentVirtual);
      return candidateDistance < bestDistance ? candidate : best;
    }, base);
  };

  class HzHybridLaneV2 {
    constructor(gallery, element) {
      this.gallery = gallery;
      this.root = gallery.root;
      this.element = element;
      this.type = element.dataset.hzHybridLane || 'primary';
      this.viewport = element.querySelector('[data-hz-hybrid-viewport]');
      this.track = element.querySelector('[data-hz-hybrid-track]');
      this.slides = Array.from(element.querySelectorAll(SLIDE_SELECTOR));
      this.count = this.slides.length;
      this.currentIndex = 0;
      this.virtualIndex = 0;
      this.slideWidth = 0;
      this.slideHeight = 0;
      this.step = 0;
      this.dragOffset = 0;
      this.previousDistances = new Map();
      this.lastRenderedVirtualIndex = 0;
      this.lastRenderedDragOffset = 0;
      this.transitionGhosts = new Set();
      this.pointerId = null;
      this.pointerStartX = 0;
      this.pointerStartY = 0;
      this.pointerStartTime = 0;
      this.pointerLastX = 0;
      this.dragStarted = false;
      this.suppressClickUntil = 0;
      this.wheelAccumulator = 0;
      this.wheelCooldownUntil = 0;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;

      this.bindEvents();
      this.bindMediaEvents();
    }

    get tileGap() {
      const styles = getComputedStyle(this.root);
      const mobile = this.gallery.mobileQuery.matches;
      const name = this.type === 'primary'
        ? (mobile ? '--hz__hybridPrimaryGapMobile' : '--hz__hybridPrimaryGapDesktop')
        : (mobile ? '--hz__hybridSecondaryGapMobile' : '--hz__hybridSecondaryGapDesktop');
      return toNumber(styles.getPropertyValue(name), mobile ? 14 : 13);
    }

    measure() {
      const reference = this.slides[0];
      if (!reference || !this.viewport || !this.track) return;

      const isMobile = this.gallery.mobileQuery.matches;
      if (isMobile) {
        const viewportWidth = this.viewport.clientWidth || window.innerWidth;
        const computedHeight = toNumber(getComputedStyle(reference).height, reference.offsetHeight);
        const peekRatio = this.gallery.mobilePeekPercent / 100;
        const denominator = 1 + (peekRatio * 2);
        const targetWidth = Math.max(1, (viewportWidth - (this.tileGap * 2)) / denominator);
        const portraitCap = computedHeight > 1 ? computedHeight * 0.96 : targetWidth;
        const mobileWidth = Math.max(1, Math.min(targetWidth, portraitCap));
        this.slides.forEach((slide) => { slide.style.width = `${mobileWidth}px`; });
      } else {
        this.slides.forEach((slide) => slide.style.removeProperty('width'));
      }

      const rect = reference.getBoundingClientRect();
      this.slideWidth = rect.width || reference.offsetWidth;
      this.slideHeight = rect.height || reference.offsetHeight;
      this.step = this.slideWidth + this.tileGap;

      if (this.slideHeight > 0) {
        this.viewport.style.height = `${this.slideHeight}px`;
        this.track.style.height = `${this.slideHeight}px`;
      }
    }

    getDistance(logicalIndex) {
      if (this.count <= 1) return 0;
      const slot = closestEquivalent(logicalIndex, this.virtualIndex, this.count);
      return slot - this.virtualIndex;
    }

    setIndex(logicalIndex, { render = true, animate = true } = {}) {
      if (!this.count) return;
      const nextLogical = mod(logicalIndex, this.count);
      this.virtualIndex = closestEquivalent(nextLogical, this.virtualIndex, this.count);
      this.currentIndex = nextLogical;
      if (render) this.render({ animate });
    }

    moveBy(delta, options = {}) {
      if (this.count <= 1 || !delta) return;
      this.virtualIndex += delta;
      this.currentIndex = mod(this.virtualIndex, this.count);
      if (options.render !== false) this.render({ animate: options.animate !== false });
    }

    setDragOffset(value) {
      this.dragOffset = Number.isFinite(value) ? value : 0;
      this.render({ animate: false, dragging: true });
    }

    clearDragOffset({ render = false } = {}) {
      this.dragOffset = 0;
      if (render) this.render({ animate: true });
    }

    createTransitionGhost(slide, startX, targetX) {
      if (!slide || !this.track || !Number.isFinite(startX) || !Number.isFinite(targetX)) return;

      const ghost = slide.cloneNode(true);
      ghost.classList.remove('hz__hybridGalleryV2Slide--active', 'hz__hybridGalleryV2Slide--loading');
      ghost.classList.add('hz__hybridGalleryV2Slide--ghost');
      ghost.removeAttribute('data-hz-hybrid-slide');
      ghost.removeAttribute('data-block-id');
      ghost.removeAttribute('data-shopify-editor-block');
      ghost.setAttribute('aria-hidden', 'true');
      ghost.setAttribute('inert', '');
      ghost.style.transition = 'none';
      ghost.style.transform = `translate3d(${startX}px, 0, 0)`;
      ghost.style.opacity = '1';
      ghost.style.zIndex = '1';
      ghost.style.pointerEvents = 'none';

      [ghost, ...ghost.querySelectorAll('*')].forEach((element) => {
        if (element.id) element.removeAttribute('id');
        Array.from(element.attributes || []).forEach((attribute) => {
          if (attribute.name.startsWith('data-shopify')) element.removeAttribute(attribute.name);
        });
      });

      ghost.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
        element.setAttribute('tabindex', '-1');
        if ('disabled' in element && element.tagName === 'BUTTON') element.disabled = true;
      });

      ghost.querySelectorAll('[data-hz-hybrid-video-controls], [data-hz-hybrid-media-loader]').forEach((element) => {
        element.hidden = true;
      });

      ghost.querySelectorAll('video').forEach((video) => {
        try { video.pause(); } catch (_) { /* no-op */ }
        video.muted = true;
        video.loop = false;
        video.preload = 'none';
        video.removeAttribute('autoplay');
        video.removeAttribute('src');
        video.querySelectorAll('source').forEach((source) => source.remove());
      });

      this.track.appendChild(ghost);
      this.transitionGhosts.add(ghost);
      ghost.getBoundingClientRect();

      requestAnimationFrame(() => {
        if (!ghost.isConnected) return;
        ghost.style.transition = `transform ${this.gallery.transitionMs}ms cubic-bezier(.22,.61,.36,1)`;
        ghost.style.transform = `translate3d(${targetX}px, 0, 0)`;
      });

      window.setTimeout(() => {
        this.transitionGhosts.delete(ghost);
        ghost.remove();
      }, Math.max(120, this.gallery.transitionMs + 120));
    }

    clearTransitionGhosts() {
      this.transitionGhosts.forEach((ghost) => ghost.remove());
      this.transitionGhosts.clear();
    }

    render({ animate = true } = {}) {
      if (!this.viewport || !this.count) return;
      if (!this.slideWidth || !this.step) this.measure();
      if (!this.slideWidth || !this.step) return;

      const viewportWidth = this.viewport.clientWidth || window.innerWidth;
      const centeredLeft = (viewportWidth - this.slideWidth) / 2;
      const wrapThreshold = Math.max(1.5, this.count / 2);
      const virtualDelta = this.virtualIndex - this.lastRenderedVirtualIndex;
      const dragDelta = this.dragOffset - this.lastRenderedDragOffset;

      this.slides.forEach((slide, logicalIndex) => {
        const distance = this.getDistance(logicalIndex);
        const previousDistance = this.previousDistances.get(slide);
        const x = centeredLeft + distance * this.step + this.dragOffset;
        const isCurrent = logicalIndex === this.currentIndex;
        const shouldTeleport = Boolean(
          animate &&
          previousDistance !== undefined &&
          Math.abs(distance - previousDistance) > wrapThreshold
        );

        if (shouldTeleport) {
          const previousX = centeredLeft + previousDistance * this.step + this.lastRenderedDragOffset;
          const outgoingTargetX = previousX - (virtualDelta * this.step) + dragDelta;
          const incomingStartX = x + (virtualDelta * this.step) - dragDelta;
          const teleportToken = `${this.virtualIndex}:${logicalIndex}:${performance.now()}`;

          this.createTransitionGhost(slide, previousX, outgoingTargetX);
          slide.dataset.hzTeleport = teleportToken;
          slide.style.transition = 'none';
          slide.style.transform = `translate3d(${incomingStartX}px, 0, 0)`;
          slide.getBoundingClientRect();

          requestAnimationFrame(() => {
            if (slide.dataset.hzTeleport !== teleportToken) return;
            slide.style.transition = `transform ${this.gallery.transitionMs}ms cubic-bezier(.22,.61,.36,1)`;
            slide.style.transform = `translate3d(${x}px, 0, 0)`;
            window.setTimeout(() => {
              if (slide.dataset.hzTeleport !== teleportToken) return;
              delete slide.dataset.hzTeleport;
              slide.style.removeProperty('transition');
            }, Math.max(120, this.gallery.transitionMs + 80));
          });
        } else {
          delete slide.dataset.hzTeleport;
          slide.style.removeProperty('transition');
          slide.style.transform = `translate3d(${x}px, 0, 0)`;
        }

        slide.style.opacity = '1';
        slide.style.zIndex = isCurrent ? '2' : '1';
        slide.classList.toggle('hz__hybridGalleryV2Slide--active', isCurrent);
        slide.setAttribute('aria-hidden', isCurrent ? 'false' : 'true');

        const link = slide.querySelector('[data-hz-hybrid-slide-link]');
        if (link) link.tabIndex = isCurrent ? 0 : -1;

        this.previousDistances.set(slide, distance);
      });

      this.lastRenderedVirtualIndex = this.virtualIndex;
      this.lastRenderedDragOffset = this.dragOffset;
      this.prepareMediaNeighborhood();
      this.updateMediaPlayback();
    }

    prepareMediaNeighborhood() {
      if (!this.count) return;
      this.slides.forEach((slide, logicalIndex) => {
        const distance = Math.abs(this.getDistance(logicalIndex));
        if (distance > 1.01) return;
        const layer = this.getCurrentMediaLayer(slide);
        const image = layer?.querySelector('img');
        if (image) {
          image.loading = 'eager';
          if ('fetchPriority' in image) image.fetchPriority = distance < 0.01 ? 'high' : 'auto';
        }
      });
    }

    getCurrentMediaLayer(slide) {
      if (!slide) return null;
      const device = this.gallery.mobileQuery.matches ? 'mobile' : 'desktop';
      return slide.querySelector(`[data-hz-media-device="${device}"]`) || slide.querySelector('[data-hz-media-device]');
    }

    getCurrentVideo(slide) {
      return this.getCurrentMediaLayer(slide)?.querySelector('.hz__hybridGalleryV2Video') || null;
    }

    getCurrentImage(slide) {
      return this.getCurrentMediaLayer(slide)?.querySelector('.hz__hybridGalleryV2Image') || null;
    }

    updateSlideLoading(slide, loading) {
      if (!slide) return;
      slide.classList.toggle('hz__hybridGalleryV2Slide--loading', Boolean(loading));
    }

    updateVideoButtons(slide, video) {
      const controls = slide?.querySelector('[data-hz-hybrid-video-controls]');
      const playButton = slide?.querySelector('[data-hz-hybrid-video-play]');
      const soundButton = slide?.querySelector('[data-hz-hybrid-video-sound]');
      if (!controls) return;

      if (!video) {
        controls.hidden = true;
        return;
      }

      controls.hidden = false;
      if (playButton) {
        const playing = !video.paused && !video.ended;
        playButton.classList.toggle('hz__hybridGalleryV2MediaButton--playing', playing);
        playButton.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
      }
      if (soundButton) {
        const unmuted = !video.muted && video.volume > 0;
        soundButton.classList.toggle('hz__hybridGalleryV2MediaButton--unmuted', unmuted);
        soundButton.setAttribute('aria-label', unmuted ? 'Mute video' : 'Unmute video');
      }
    }

    updateMediaPlayback() {
      this.slides.forEach((slide, logicalIndex) => {
        const currentLayer = this.getCurrentMediaLayer(slide);
        const currentVideo = currentLayer?.querySelector('.hz__hybridGalleryV2Video') || null;
        const currentImage = currentLayer?.querySelector('.hz__hybridGalleryV2Image') || null;
        const isActive = logicalIndex === this.currentIndex;

        slide.querySelectorAll('.hz__hybridGalleryV2Video').forEach((video) => {
          video.loop = false;
          if (video !== currentVideo || !isActive) {
            if (!video.paused) video.pause();
            video.muted = true;
            delete video.dataset.hzUserPaused;
            delete video.dataset.hzUserUnmuted;
            delete video.dataset.hzCompleted;
            this.gallery.releaseVideoHold(video, { sync: false });
            if (video.readyState > HTMLMediaElement.HAVE_METADATA) {
              try { video.currentTime = 0; } catch (_) { /* no-op */ }
            }
          }
        });

        if (!isActive) {
          this.updateVideoButtons(slide, null);
          this.updateSlideLoading(slide, false);
          return;
        }

        if (currentVideo) {
          currentVideo.loop = false;
          currentVideo.preload = 'auto';
          if (currentVideo.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) {
            this.updateSlideLoading(slide, true);
            if (currentVideo.networkState === HTMLMediaElement.NETWORK_EMPTY) {
              try { currentVideo.load(); } catch (_) { /* no-op */ }
            }
          } else {
            this.updateSlideLoading(slide, false);
          }

          this.updateVideoButtons(slide, currentVideo);

          const canAutoPlayMedia = Boolean(
            this.gallery.isInView &&
            !document.hidden &&
            !this.gallery.userPaused &&
            currentVideo.dataset.hzCompleted !== 'true'
          );

          if (canAutoPlayMedia && currentVideo.dataset.hzUserPaused !== 'true') {
            currentVideo.muted = currentVideo.dataset.hzUserUnmuted !== 'true';
            if (currentVideo.paused) {
              this.gallery.requestVideoHold(currentVideo, this);
              const playPromise = currentVideo.play();
              if (playPromise && typeof playPromise.catch === 'function') {
                playPromise.catch(() => {
                  this.gallery.releaseVideoHold(currentVideo);
                  this.updateSlideLoading(slide, false);
                  this.updateVideoButtons(slide, currentVideo);
                });
              }
            }
          } else if (this.gallery.userPaused && !currentVideo.paused) {
            currentVideo.pause();
          }
          return;
        }

        this.updateVideoButtons(slide, null);
        if (currentImage && !currentImage.complete) this.updateSlideLoading(slide, true);
        else this.updateSlideLoading(slide, false);
      });

      this.gallery.syncVideoHold();
    }

    bindMediaEvents() {
      this.slides.forEach((slide) => {
        slide.querySelectorAll('.hz__hybridGalleryV2Video').forEach((video) => {
          video.loop = false;

          const setLoading = () => {
            if (this.getCurrentVideo(this.slides[this.currentIndex]) === video && this.gallery.isInView) {
              this.updateSlideLoading(slide, true);
            }
          };
          const setReady = () => {
            this.updateSlideLoading(slide, false);
            this.updateVideoButtons(slide, video);
          };
          const syncButtons = () => this.updateVideoButtons(slide, video);
          const handlePlay = () => {
            video.loop = false;
            this.gallery.requestVideoHold(video, this);
            syncButtons();
          };
          const handlePause = () => {
            this.gallery.releaseVideoHold(video);
            syncButtons();
          };
          const handleEnded = () => {
            video.loop = false;
            video.dataset.hzCompleted = 'true';
            this.gallery.handleVideoEnded(video);
            syncButtons();
          };

          video.addEventListener('loadstart', setLoading, { signal: this.signal });
          video.addEventListener('waiting', setLoading, { signal: this.signal });
          video.addEventListener('stalled', setLoading, { signal: this.signal });
          video.addEventListener('canplay', setReady, { signal: this.signal });
          video.addEventListener('playing', setReady, { signal: this.signal });
          video.addEventListener('play', handlePlay, { signal: this.signal });
          video.addEventListener('pause', handlePause, { signal: this.signal });
          video.addEventListener('ended', handleEnded, { signal: this.signal });
          video.addEventListener('volumechange', syncButtons, { signal: this.signal });
        });

        slide.querySelectorAll('.hz__hybridGalleryV2Image').forEach((image) => {
          const settle = () => {
            if (this.getCurrentImage(this.slides[this.currentIndex]) === image) {
              this.updateSlideLoading(slide, false);
            }
          };
          image.addEventListener('load', settle, { signal: this.signal });
          image.addEventListener('error', settle, { signal: this.signal });
        });
      });
    }

    bindEvents() {
      if (!this.viewport) return;

      this.viewport.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal: this.signal });
      this.viewport.addEventListener('pointermove', (event) => this.onPointerMove(event), { signal: this.signal });
      this.viewport.addEventListener('pointerup', (event) => this.onPointerUp(event), { signal: this.signal });
      this.viewport.addEventListener('pointercancel', (event) => this.onPointerCancel(event), { signal: this.signal });
      this.viewport.addEventListener('lostpointercapture', (event) => this.onPointerCancel(event), { signal: this.signal });
      this.viewport.addEventListener('keydown', (event) => this.onKeyDown(event), { signal: this.signal });
      this.viewport.addEventListener('wheel', (event) => this.onWheel(event), { passive: false, signal: this.signal });
      this.viewport.addEventListener(
        'click',
        (event) => {
          if (Date.now() < this.suppressClickUntil) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        },
        { capture: true, signal: this.signal }
      );

      this.element.addEventListener(
        'click',
        (event) => {
          const playButton = event.target.closest?.('[data-hz-hybrid-video-play]');
          if (playButton) {
            event.preventDefault();
            event.stopPropagation();
            this.toggleCurrentVideoPlayback(playButton.closest(SLIDE_SELECTOR));
            return;
          }

          const soundButton = event.target.closest?.('[data-hz-hybrid-video-sound]');
          if (soundButton) {
            event.preventDefault();
            event.stopPropagation();
            this.toggleCurrentVideoSound(soundButton.closest(SLIDE_SELECTOR));
          }
        },
        { signal: this.signal }
      );
    }

    onPointerDown(event) {
      if (!this.gallery.enableDrag || this.count <= 1 || event.button !== 0) return;
      if (event.target.closest?.('a, button, input, select, textarea, summary, [contenteditable="true"]')) return;
      if (event.target.closest?.('[data-hz-hybrid-video-controls]')) return;

      this.pointerId = event.pointerId;
      this.pointerStartX = event.clientX;
      this.pointerStartY = event.clientY;
      this.pointerLastX = event.clientX;
      this.pointerStartTime = performance.now();
      this.dragStarted = false;
      try { this.viewport.setPointerCapture(event.pointerId); } catch (_) { /* no-op */ }
    }

    onPointerMove(event) {
      if (this.pointerId !== event.pointerId) return;
      const deltaX = event.clientX - this.pointerStartX;
      const deltaY = event.clientY - this.pointerStartY;
      this.pointerLastX = event.clientX;

      if (!this.dragStarted) {
        if (Math.abs(deltaX) < 5) return;
        if (Math.abs(deltaY) > Math.abs(deltaX)) {
          this.releasePointer();
          return;
        }
        this.dragStarted = true;
        this.gallery.beginDrag(this);
      }

      if (event.cancelable) event.preventDefault();
      this.gallery.updateDrag(this, deltaX);
    }

    onPointerUp(event) {
      if (this.pointerId !== event.pointerId) return;
      const elapsed = Math.max(1, performance.now() - this.pointerStartTime);
      const deltaX = event.clientX - this.pointerStartX;
      const velocity = deltaX / elapsed;
      const threshold = Math.max(36, this.slideWidth * DRAG_DISTANCE_RATIO);
      let direction = 0;

      if (this.dragStarted) {
        if (Math.abs(deltaX) >= threshold || Math.abs(velocity) >= DRAG_VELOCITY) {
          direction = deltaX < 0 ? 1 : -1;
        }
        this.suppressClickUntil = Date.now() + 280;
        this.gallery.finishDrag(this, direction);
      }

      this.releasePointer();
    }

    onPointerCancel(event) {
      if (this.pointerId === null || (event.pointerId !== undefined && this.pointerId !== event.pointerId)) return;
      if (this.dragStarted) this.gallery.finishDrag(this, 0);
      this.releasePointer();
    }

    releasePointer() {
      if (this.pointerId !== null) {
        try {
          if (this.viewport.hasPointerCapture?.(this.pointerId)) this.viewport.releasePointerCapture(this.pointerId);
        } catch (_) { /* no-op */ }
      }
      this.pointerId = null;
      this.dragStarted = false;
    }

    onKeyDown(event) {
      if (event.target.closest?.('button, a, input, select, textarea')) return;
      let direction = 0;
      if (event.key === 'ArrowRight') direction = this.gallery.isRtl ? -1 : 1;
      if (event.key === 'ArrowLeft') direction = this.gallery.isRtl ? 1 : -1;

      if (direction) {
        event.preventDefault();
        this.gallery.navigateFromLane(this, direction, { source: 'keyboard' });
        return;
      }

      if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        const index = event.key === 'Home' ? 0 : Math.max(0, this.count - 1);
        this.gallery.navigateLaneToIndex(this, index, { source: 'keyboard' });
      }
    }

    onWheel(event) {
      if (!this.gallery.enableTrackpad || this.count <= 1 || Date.now() < this.wheelCooldownUntil) return;
      if (Math.abs(event.deltaX) < 2 || Math.abs(event.deltaX) <= Math.abs(event.deltaY) * 1.1) return;

      event.preventDefault();
      this.wheelAccumulator += event.deltaX;
      if (Math.abs(this.wheelAccumulator) < WHEEL_THRESHOLD) return;

      const direction = this.wheelAccumulator > 0 ? 1 : -1;
      this.wheelAccumulator = 0;
      this.wheelCooldownUntil = Date.now() + WHEEL_COOLDOWN;
      this.gallery.navigateFromLane(this, direction, { source: 'trackpad' });
    }

    toggleCurrentVideoPlayback(slide) {
      if (!slide) return;
      const video = this.getCurrentVideo(slide);
      if (!video) return;

      video.loop = false;
      if (video.paused || video.ended) {
        if (video.ended || video.dataset.hzCompleted === 'true') {
          delete video.dataset.hzCompleted;
          try { video.currentTime = 0; } catch (_) { /* no-op */ }
        }
        delete video.dataset.hzUserPaused;
        video.preload = 'auto';
        this.gallery.requestVideoHold(video, this);
        const promise = video.play();
        if (promise && typeof promise.catch === 'function') {
          promise.catch(() => this.gallery.releaseVideoHold(video));
        }
      } else {
        video.dataset.hzUserPaused = 'true';
        video.pause();
      }
      this.updateVideoButtons(slide, video);
    }

    toggleCurrentVideoSound(slide) {
      if (!slide) return;
      const video = this.getCurrentVideo(slide);
      if (!video) return;

      if (video.muted || video.volume === 0) {
        this.gallery.muteAllVideosExcept(video);
        video.muted = false;
        video.dataset.hzUserUnmuted = 'true';
      } else {
        video.muted = true;
        delete video.dataset.hzUserUnmuted;
      }
      this.updateVideoButtons(slide, video);
    }

    destroy() {
      this.abortController.abort();
      this.clearTransitionGhosts();
      this.slides.forEach((slide) => {
        slide.removeAttribute('style');
        slide.removeAttribute('data-hz-teleport');
        slide.classList.remove('hz__hybridGalleryV2Slide--active', 'hz__hybridGalleryV2Slide--loading');
        slide.querySelectorAll('.hz__hybridGalleryV2Video').forEach((video) => {
          video.pause();
          video.muted = true;
        });
      });
      this.viewport?.removeAttribute('style');
      this.track?.removeAttribute('style');
    }
  }

  class HzHybridGalleryV2 {
    constructor(root) {
      this.root = root;
      this.mobileQuery = window.matchMedia(MOBILE_QUERY);
      this.reducedMotionQuery = window.matchMedia(REDUCED_MOTION_QUERY);
      this.autoplayEnabled = toBoolean(root.dataset.hzAutoplay, true);
      this.startPaused = toBoolean(root.dataset.hzStartPaused, false);
      this.syncInteractions = toBoolean(root.dataset.hzSyncInteractions, true);
      this.enableDrag = toBoolean(root.dataset.hzDrag, true);
      this.enableTrackpad = toBoolean(root.dataset.hzTrackpad, true);
      this.transitionMs = toNumber(root.dataset.hzTransitionMs, 800);
      this.mobilePeekPercent = Math.min(30, Math.max(10, toNumber(root.dataset.hzMobilePeek, 20)));
      this.navCount = Math.max(0, Number.parseInt(root.dataset.hzNavCount || '0', 10));
      this.primaryCount = Math.max(0, Number.parseInt(root.dataset.hzPrimaryCount || '0', 10));
      this.secondaryCount = Math.max(0, Number.parseInt(root.dataset.hzSecondaryCount || '0', 10));
      this.isRtl = document.documentElement.dir === 'rtl';
      this.userPaused = this.startPaused || this.reducedMotionQuery.matches;
      this.videoHold = false;
      this.videoHoldVideos = new Set();
      this.isInView = false;
      this.isDragging = false;
      this.masterIndex = 0;
      this.masterVirtual = 0;
      this.elapsedMs = 0;
      this.lastClockTime = null;
      this.rafId = 0;
      this.resizeRaf = 0;
      this.destroyed = false;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.dots = Array.from(root.querySelectorAll('[data-hz-hybrid-dot]'));
      this.dotFills = Array.from(root.querySelectorAll('[data-hz-hybrid-dot-fill]'));
      this.autoplayButton = root.querySelector('[data-hz-hybrid-autoplay]');
      this.status = root.querySelector('[data-hz-hybrid-status]');
      this.lanes = Array.from(root.querySelectorAll(LANE_SELECTOR)).map((element) => new HzHybridLaneV2(this, element));
      this.primaryLane = this.lanes.find((lane) => lane.type === 'primary') || null;
      this.secondaryLane = this.lanes.find((lane) => lane.type === 'secondary') || null;

      this.bindEvents();
      this.createObservers();
      this.root.classList.add('hz__hybridGalleryV2--instant', 'hz__hybridGalleryV2--ready');
      this.updatePausedUi();
      this.updateDots(0);

      requestAnimationFrame(() => {
        if (this.destroyed) return;
        this.measureAndRender({ animate: false });
        requestAnimationFrame(() => {
          this.root.classList.remove('hz__hybridGalleryV2--instant');
          this.updateAllMediaPlayback();
          this.syncClock();
        });
      });
    }

    bindEvents() {
      this.dots.forEach((dot) => {
        dot.addEventListener(
          'click',
          () => {
            const index = Number.parseInt(dot.dataset.hzIndex || '0', 10);
            this.goMaster(index, { source: 'dot', announce: true });
          },
          { signal: this.signal }
        );
        dot.addEventListener('keydown', (event) => this.onDotKeyDown(event, dot), { signal: this.signal });
      });

      this.autoplayButton?.addEventListener(
        'click',
        () => this.toggleGalleryPlayback(),
        { signal: this.signal }
      );

      this.mobileQuery.addEventListener?.('change', () => this.onResponsiveChange(), { signal: this.signal });
      this.reducedMotionQuery.addEventListener?.('change', (event) => {
        if (event.matches) this.setUserPaused(true, { announce: false });
        this.measureAndRender({ animate: false });
      }, { signal: this.signal });

      document.addEventListener('visibilitychange', () => {
        this.lastClockTime = null;
        this.updateAllMediaPlayback();
        this.syncClock();
      }, { signal: this.signal });
    }

    createObservers() {
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          const nextInView = Boolean(entry?.isIntersecting && entry.intersectionRatio >= 0.12);
          if (nextInView === this.isInView) return;
          this.isInView = nextInView;
          this.lastClockTime = null;
          this.updateAllMediaPlayback();
          this.syncClock();
        },
        { threshold: [0, 0.12, 0.35] }
      );
      this.intersectionObserver.observe(this.root);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
        this.resizeObserver.observe(this.root);
        this.lanes.forEach((lane) => lane.viewport && this.resizeObserver.observe(lane.viewport));
      } else {
        window.addEventListener('resize', () => this.scheduleMeasure(), { signal: this.signal });
      }
    }

    onResponsiveChange() {
      this.lanes.forEach((lane) => {
        lane.slides.forEach((slide) => {
          slide.querySelectorAll('.hz__hybridGalleryV2Video').forEach((video) => {
            video.pause();
            video.muted = true;
          });
        });
      });
      this.scheduleMeasure(true);
    }

    scheduleMeasure(instant = false) {
      if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
      this.resizeRaf = requestAnimationFrame(() => {
        this.resizeRaf = 0;
        this.measureAndRender({ animate: !instant && !this.reducedMotionQuery.matches });
      });
    }

    measureAndRender({ animate = true } = {}) {
      if (this.destroyed) return;
      if (!animate) this.root.classList.add('hz__hybridGalleryV2--instant');
      this.lanes.forEach((lane) => {
        lane.measure();
        lane.render({ animate });
      });
      if (!animate) requestAnimationFrame(() => this.root.classList.remove('hz__hybridGalleryV2--instant'));
    }

    beginDrag() {
      if (this.isDragging) return;
      this.isDragging = true;
      this.root.classList.add('hz__hybridGalleryV2--dragging');
      this.lastClockTime = null;
      this.stopClock();
      this.lanes.forEach((lane) => {
        lane.clearTransitionGhosts();
        lane.viewport?.classList.add('hz__hybridGalleryV2Viewport--dragging');
      });
    }

    updateDrag(sourceLane, deltaX) {
      if (!this.isDragging) this.beginDrag(sourceLane);
      sourceLane.setDragOffset(deltaX);

      if (this.syncInteractions && sourceLane.step > 0) {
        const normalized = deltaX / sourceLane.step;
        this.lanes.forEach((lane) => {
          if (lane === sourceLane || lane.step <= 0) return;
          lane.setDragOffset(normalized * lane.step);
        });
      }
    }

    finishDrag(sourceLane, direction) {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.root.classList.remove('hz__hybridGalleryV2--dragging');
      this.lanes.forEach((lane) => lane.viewport?.classList.remove('hz__hybridGalleryV2Viewport--dragging'));

      if (direction) {
        this.navigateFromLane(sourceLane, direction, { source: 'drag', render: false });
      } else {
        this.resetTimer({ preserveProgress: false });
      }

      this.lanes.forEach((lane) => lane.clearDragOffset());
      this.lanes.forEach((lane) => lane.render({ animate: !this.reducedMotionQuery.matches }));
      this.updateAllMediaPlayback();
      this.syncClock();
    }

    navigateFromLane(lane, direction, { source = 'manual', render = true } = {}) {
      if (!lane || !direction) return;

      if (this.syncInteractions) {
        this.goMaster(this.masterIndex + direction, { source, announce: source !== 'trackpad', render });
        return;
      }

      if (lane === this.primaryLane) {
        lane.moveBy(direction, { render, animate: !this.reducedMotionQuery.matches });
        this.masterVirtual += direction;
        this.masterIndex = mod(this.masterVirtual, Math.max(1, this.navCount));
        this.updateDots(this.masterIndex);
        this.resetTimer({ preserveProgress: false });
        if (source !== 'trackpad') this.announceCurrent();
      } else {
        lane.moveBy(direction, { render, animate: !this.reducedMotionQuery.matches });
        this.resetTimer({ preserveProgress: false });
      }

      if (render) this.updateAllMediaPlayback();
      this.syncClock();
    }

    navigateLaneToIndex(lane, index, { source = 'manual' } = {}) {
      if (!lane?.count) return;
      if (this.syncInteractions) {
        this.goMaster(index, { source, announce: true });
        return;
      }

      lane.setIndex(index, { animate: !this.reducedMotionQuery.matches });
      if (lane === this.primaryLane) {
        this.masterIndex = mod(index, Math.max(1, this.navCount));
        this.masterVirtual = closestEquivalent(this.masterIndex, this.masterVirtual, Math.max(1, this.navCount));
        this.updateDots(this.masterIndex);
      }
      this.resetTimer({ preserveProgress: false });
      this.announceCurrent();
      this.updateAllMediaPlayback();
      this.syncClock();
    }

    goMaster(index, { source = 'manual', announce = false, render = true } = {}) {
      if (this.navCount <= 0) return;
      const nextIndex = mod(index, this.navCount);
      this.masterVirtual = closestEquivalent(nextIndex, this.masterVirtual, this.navCount);
      this.masterIndex = nextIndex;

      this.lanes.forEach((lane) => {
        if (!lane.count) return;
        lane.setIndex(mod(nextIndex, lane.count), { render, animate: !this.reducedMotionQuery.matches });
      });

      this.updateDots(nextIndex);
      this.resetTimer({ preserveProgress: false });
      if (announce && source !== 'auto') this.announceCurrent();
      if (render) this.updateAllMediaPlayback();
      if (source !== 'auto') this.syncClock();
    }

    updateDots(index) {
      this.dots.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.classList.toggle('hz__hybridGalleryV2Dot--active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
        dot.tabIndex = active ? 0 : -1;
      });
      this.dots.forEach((dot, dotIndex) => {
        if (dotIndex !== index) dot.style.setProperty('--hz__hybridDotProgress', '0');
      });
      this.dotFills.forEach((fill, fillIndex) => {
        if (fillIndex !== index) fill.style.transform = 'scaleX(0)';
      });
      this.renderProgress(this.elapsedMs / this.getCurrentDurationMs());
    }

    onDotKeyDown(event, dot) {
      const currentIndex = Number.parseInt(dot.dataset.hzIndex || '0', 10);
      let nextIndex = null;
      if (event.key === 'ArrowRight') nextIndex = currentIndex + (this.isRtl ? -1 : 1);
      if (event.key === 'ArrowLeft') nextIndex = currentIndex + (this.isRtl ? 1 : -1);
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.navCount - 1;
      if (nextIndex === null) return;
      event.preventDefault();
      nextIndex = mod(nextIndex, this.navCount);
      this.goMaster(nextIndex, { source: 'dot-keyboard', announce: true });
      this.dots[nextIndex]?.focus({ preventScroll: true });
    }

    isActiveVideo(video) {
      if (!video) return false;
      return this.lanes.some((lane) => {
        const slide = lane.slides[lane.currentIndex];
        return lane.getCurrentVideo(slide) === video;
      });
    }

    requestVideoHold(video, lane = null) {
      if (!video) return;
      if (lane) {
        const activeSlide = lane.slides[lane.currentIndex];
        if (lane.getCurrentVideo(activeSlide) !== video) return;
      } else if (!this.isActiveVideo(video)) {
        return;
      }

      video.loop = false;
      this.videoHoldVideos.add(video);
      this.syncVideoHold();
    }

    releaseVideoHold(video, { sync = true } = {}) {
      if (video) this.videoHoldVideos.delete(video);
      if (sync) this.syncVideoHold();
    }

    handleVideoEnded(video) {
      if (!video) return;
      video.loop = false;
      this.videoHoldVideos.delete(video);
      this.syncVideoHold();
    }

    syncVideoHold() {
      const nextHold = this.videoHoldVideos.size > 0;
      if (nextHold === this.videoHold) return;
      this.videoHold = nextHold;
      this.lastClockTime = null;
      this.updatePausedUi();
      this.syncClock();
    }

    toggleGalleryPlayback() {
      if (this.videoHold) {
        const heldVideos = Array.from(this.videoHoldVideos);
        this.videoHoldVideos.clear();
        this.videoHold = false;
        this.userPaused = false;

        heldVideos.forEach((video) => {
          video.loop = false;
          video.dataset.hzUserPaused = 'true';
          if (!video.paused) video.pause();
        });

        this.lastClockTime = null;
        this.updatePausedUi();
        this.updateAllMediaPlayback();
        this.syncClock();
        if (this.status) this.status.textContent = 'Gallery playing. Video paused.';
        return;
      }

      this.setUserPaused(!this.userPaused);
    }

    setUserPaused(paused, { announce = true } = {}) {
      this.userPaused = Boolean(paused);
      this.lastClockTime = null;
      this.updatePausedUi();
      this.updateAllMediaPlayback();
      this.syncClock();
      if (announce && this.status) this.status.textContent = this.userPaused ? 'Gallery paused.' : 'Gallery playing.';
    }

    updatePausedUi() {
      const paused = this.userPaused || this.videoHold;
      this.root.classList.toggle('hz__hybridGalleryV2--paused', paused);
      this.root.classList.toggle('hz__hybridGalleryV2--videoHold', this.videoHold);
      if (!this.autoplayButton) return;
      this.autoplayButton.setAttribute('aria-label', paused ? 'Play gallery' : 'Pause gallery');
      this.autoplayButton.setAttribute('aria-pressed', paused ? 'true' : 'false');
      this.autoplayButton.dataset.hzPauseReason = this.videoHold ? 'video' : this.userPaused ? 'user' : 'playing';
    }

    getCurrentDurationMs() {
      const primarySlide = this.primaryLane?.slides[this.primaryLane.currentIndex];
      const secondarySlide = this.secondaryLane?.slides[this.secondaryLane.currentIndex];
      const seconds = toNumber(primarySlide?.dataset.hzDuration, toNumber(secondarySlide?.dataset.hzDuration, 5));
      return Math.max(2000, seconds * 1000);
    }

    shouldRunClock() {
      return Boolean(
        this.autoplayEnabled &&
        this.navCount > 1 &&
        !this.userPaused &&
        !this.videoHold &&
        !this.isDragging &&
        this.isInView &&
        !document.hidden
      );
    }

    syncClock() {
      if (this.shouldRunClock()) this.startClock();
      else this.stopClock();
    }

    startClock() {
      if (this.rafId || !this.shouldRunClock()) return;
      this.rafId = requestAnimationFrame((time) => this.tick(time));
    }

    stopClock() {
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.rafId = 0;
      this.lastClockTime = null;
    }

    tick(time) {
      this.rafId = 0;
      if (!this.shouldRunClock()) {
        this.lastClockTime = null;
        return;
      }

      if (this.lastClockTime === null) this.lastClockTime = time;
      const delta = Math.min(100, Math.max(0, time - this.lastClockTime));
      this.lastClockTime = time;
      this.elapsedMs += delta;

      const duration = this.getCurrentDurationMs();
      const progress = Math.min(1, this.elapsedMs / duration);
      this.renderProgress(progress);

      if (progress >= 1) {
        this.elapsedMs = 0;
        this.lastClockTime = time;
        this.advanceAutoplay();
      }

      if (this.shouldRunClock()) this.rafId = requestAnimationFrame((nextTime) => this.tick(nextTime));
    }


    advanceAutoplay() {
      if (this.syncInteractions) {
        this.goMaster(this.masterIndex + 1, { source: 'auto', announce: false });
        return;
      }

      this.lanes.forEach((lane) => {
        if (lane.count > 1) lane.moveBy(1, { animate: !this.reducedMotionQuery.matches });
      });

      this.masterVirtual += 1;
      this.masterIndex = mod(this.masterVirtual, Math.max(1, this.navCount));
      this.updateDots(this.masterIndex);
      this.resetTimer({ preserveProgress: false });
      this.updateAllMediaPlayback();
    }

    renderProgress(progress) {
      const activeDot = this.dots[this.masterIndex];
      const activeFill = this.dotFills[this.masterIndex];
      if (!activeDot || !activeFill) return;
      const clamped = Math.max(0, Math.min(1, Number.isFinite(progress) ? progress : 0));
      const value = clamped.toFixed(5);
      activeDot.style.setProperty('--hz__hybridDotProgress', value);
      activeFill.style.transform = `scaleX(${value})`;
    }

    resetTimer({ preserveProgress = false } = {}) {
      if (!preserveProgress) this.elapsedMs = 0;
      this.lastClockTime = null;
      this.renderProgress(this.elapsedMs / this.getCurrentDurationMs());
    }

    announceCurrent() {
      if (!this.status || this.navCount < 1) return;
      this.status.textContent = `Slide set ${this.masterIndex + 1} of ${this.navCount}.`;
    }

    muteAllVideosExcept(exceptVideo) {
      this.root.querySelectorAll('.hz__hybridGalleryV2Video').forEach((video) => {
        if (video === exceptVideo) return;
        video.muted = true;
        delete video.dataset.hzUserUnmuted;
      });
      this.lanes.forEach((lane) => {
        const activeSlide = lane.slides[lane.currentIndex];
        const activeVideo = lane.getCurrentVideo(activeSlide);
        if (activeSlide && activeVideo) lane.updateVideoButtons(activeSlide, activeVideo);
      });
    }

    updateAllMediaPlayback() {
      this.lanes.forEach((lane) => lane.updateMediaPlayback());
    }

    selectBlock(blockId) {
      if (!blockId) return;
      for (const lane of this.lanes) {
        const slide = lane.slides.find((item) => item.dataset.blockId === blockId);
        if (!slide) continue;
        const index = Number.parseInt(slide.dataset.hzIndex || '0', 10);
        this.setUserPaused(true, { announce: false });
        if (this.syncInteractions) this.goMaster(index, { source: 'editor', announce: false });
        else this.navigateLaneToIndex(lane, index, { source: 'editor' });
        return;
      }
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      this.stopClock();
      if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
      this.abortController.abort();
      this.intersectionObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.videoHoldVideos.clear();
      this.videoHold = false;
      this.lanes.forEach((lane) => lane.destroy());
      this.root.classList.remove(
        'hz__hybridGalleryV2--ready',
        'hz__hybridGalleryV2--instant',
        'hz__hybridGalleryV2--dragging',
        'hz__hybridGalleryV2--paused',
        'hz__hybridGalleryV2--videoHold'
      );
      this.dotFills.forEach((fill) => fill.removeAttribute('style'));
      this.dots.forEach((dot) => dot.style.removeProperty('--hz__hybridDotProgress'));
    }
  }

  const init = (scope = document) => {
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));

    roots.forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new HzHybridGalleryV2(root));
    });
  };

  const destroy = (scope) => {
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));

    roots.forEach((root) => {
      const instance = instances.get(root);
      if (!instance) return;
      instance.destroy();
      instances.delete(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const blockId = event.detail?.blockId;
    const root = event.target?.closest?.(ROOT_SELECTOR) || document.querySelector(`${ROOT_SELECTOR} [data-block-id="${CSS.escape(blockId || '')}"]`)?.closest(ROOT_SELECTOR);
    const instance = root ? instances.get(root) : null;
    instance?.selectBlock(blockId);
  });
})();

/* ========================================================================== 
   AR Header V2 — mega menu, mobile panel stack, predictive search
   ========================================================================== */
(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-hz-header-v2]';
  const instances = new WeakMap();
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktopQuery = window.matchMedia('(min-width: 1024px)');
  const coarsePointerQuery = window.matchMedia('(hover: none), (pointer: coarse)');

  const toNumber = (value, fallback = 0) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const sleep = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

  class HzHeaderV2 {
    constructor(root) {
      this.root = root;
      this.destroyed = false;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.openDelay = toNumber(root.dataset.hzOpenDelay, 80);
      this.closeDelay = toNumber(root.dataset.hzCloseDelay, 180);
      this.searchDebounce = toNumber(root.dataset.hzSearchDebounce, 180);
      this.searchMinChars = Math.max(1, toNumber(root.dataset.hzSearchMinChars, 2));
      this.predictiveUrl = root.dataset.hzPredictiveUrl || '/search/suggest';
      this.searchUrl = root.dataset.hzSearchUrl || '/search';
      this.searchProductLimit = Math.min(8, Math.max(2, toNumber(root.dataset.hzSearchProductLimit, 6)));
      this.searchSuggestionLimit = Math.min(6, Math.max(2, toNumber(root.dataset.hzSearchSuggestionLimit, 4)));
      this.searchShowCollections = root.dataset.hzSearchShowCollections !== 'false';
      this.searchShowPages = root.dataset.hzSearchShowPages !== 'false';
      this.searchShowArticles = root.dataset.hzSearchShowArticles === 'true';
      this.searchUnavailableProducts = ['show', 'hide', 'last'].includes(root.dataset.hzSearchUnavailable) ? root.dataset.hzSearchUnavailable : 'last';
      this.moneyFormat = root.dataset.hzMoneyFormat || window.money_format || '${{amount}}';
      this.searchCache = new Map();
      this.searchCacheMax = 24;
      this.sticky = root.dataset.hzSticky === 'true';
      this.hideOnScroll = root.dataset.hzHideScroll === 'true';
      this.mobilePanelDuration = toNumber(getComputedStyle(root).getPropertyValue('--hz-headerMobilePanelDuration'), 340);

      this.bar = root.querySelector('[data-hz-header-bar]');
      this.topNavList = root.querySelector('[data-hz-top-nav-list]');
      this.topItems = Array.from(root.querySelectorAll('[data-hz-top-item]'));
      this.topLinks = Array.from(root.querySelectorAll('[data-hz-top-link]'));
      this.indicator = root.querySelector('[data-hz-menu-indicator]');
      this.backdrop = root.querySelector('[data-hz-header-backdrop]');
      this.currentLink = this.topLinks.find((link) => link.dataset.hzCurrent === 'true') || null;
      this.hoveredLink = null;
      this.openItem = null;
      this.openTimer = 0;
      this.closeTimer = 0;
      this.visualSwitchToken = 0;
      this.leftHistories = new WeakMap();
      this.megaHideTimers = new WeakMap();

      this.mobileMenu = root.querySelector('[data-hz-mobile-menu]');
      this.mobileOpenButton = root.querySelector('[data-hz-mobile-menu-open]');
      this.mobileCloseButton = root.querySelector('[data-hz-mobile-menu-close]');
      this.mobilePanels = Array.from(root.querySelectorAll('[data-hz-mobile-panel]'));
      this.mobileHistory = ['root'];
      this.mobileOpener = null;

      this.searchToggle = root.querySelector('[data-hz-search-toggle]');
      this.searchDock = root.querySelector('[data-hz-search-dock]');
      this.searchPanel = root.querySelector('[data-hz-search-panel]');
      this.searchForm = root.querySelector('[data-hz-search-form]');
      this.searchInput = root.querySelector('[data-hz-search-input]');
      this.searchClear = root.querySelector('[data-hz-search-clear]');
      this.searchClose = root.querySelector('[data-hz-search-close]');
      this.searchInitial = root.querySelector('[data-hz-search-initial]');
      this.searchDynamic = root.querySelector('[data-hz-search-dynamic]');
      this.searchLoader = root.querySelector('[data-hz-search-loader]');
      this.searchResults = root.querySelector('[data-hz-search-results]');
      this.searchStatus = root.querySelector('[data-hz-search-status]');
      this.searchRequestController = null;
      this.searchRequestSerial = 0;
      this.searchTimer = 0;
      this.searchOpener = null;

      this.lastScrollY = window.scrollY;
      this.scrollRaf = 0;
      this.resizeRaf = 0;
      this.scrollLocked = false;

      this.cartObserver = new MutationObserver(() => this.syncCartCount());
      this.resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(() => this.scheduleMeasure()) : null;

      this.init();
    }

    init() {
      this.root.classList.add('hz__siteHeaderV2--ready');
      this.setupTopNavigation();
      this.setupMegaMenuDelegation();
      this.setupMobileMenu();
      this.setupSearch();
      this.setupGlobalEvents();
      this.setupSticky();
      this.setupCartObserver();
      this.resetMobilePanels();
      this.syncCartCount();
      this.scheduleMeasure();

      this.resizeObserver?.observe(this.root);
      if (this.topNavList) this.resizeObserver?.observe(this.topNavList);

      if (document.fonts?.ready) {
        document.fonts.ready.then(() => {
          if (!this.destroyed) this.scheduleMeasure();
        });
      }
    }

    setupTopNavigation() {
      this.topItems.forEach((item) => {
        const link = item.querySelector(':scope > [data-hz-top-link]');
        if (!link) return;
        const mega = item.querySelector(':scope > [data-hz-mega-menu]');
        const hasMega = Boolean(mega && link.hasAttribute('data-hz-mega-trigger'));

        // Bind hover intent to the actual top-level link, not the <li>. The mega
        // menu is an absolutely positioned descendant of that <li>; listening on
        // the ancestor made the descendant's large rectangle behave like an
        // extension of the navigation trigger.
        link.addEventListener('pointerenter', () => {
          if (!desktopQuery.matches) return;
          this.hoveredLink = link;
          this.moveIndicator(link, true);
          if (hasMega) {
            this.prepareMega(item);
            this.scheduleOpen(item);
          } else {
            this.scheduleClose(this.openItem, 60);
          }
        }, { signal: this.signal });

        link.addEventListener('pointerleave', (event) => {
          if (!desktopQuery.matches || !hasMega) return;
          const next = event.relatedTarget;
          if (next && mega.contains(next)) return;
          this.scheduleClose(item);
        }, { signal: this.signal });

        if (mega) {
          mega.addEventListener('pointerenter', () => {
            if (!desktopQuery.matches) return;
            window.clearTimeout(this.closeTimer);
          }, { signal: this.signal });

          mega.addEventListener('pointerleave', (event) => {
            if (!desktopQuery.matches) return;
            const next = event.relatedTarget;
            if (next && link.contains(next)) return;
            this.scheduleClose(item);
          }, { signal: this.signal });
        }

        item.addEventListener('focusout', () => {
          if (!desktopQuery.matches || !hasMega) return;
          requestAnimationFrame(() => {
            if (this.destroyed || this.pointerOrFocusInsideItem(item)) return;
            if (this.openItem === item) this.closeMega(item);
          });
        }, { signal: this.signal });

        link.addEventListener('focus', () => {
          if (!desktopQuery.matches) return;
          this.hoveredLink = link;
          this.moveIndicator(link, true);
          if (hasMega) {
            this.prepareMega(item);
            this.openMega(item, { focusFirst: false });
          } else {
            this.closeMega(this.openItem, { restoreIndicator: false });
          }
        }, { signal: this.signal });

        if (hasMega) {
          link.addEventListener('click', (event) => {
            if (!desktopQuery.matches || !coarsePointerQuery.matches) return;
            if (this.openItem !== item) {
              event.preventDefault();
              this.openMega(item, { focusFirst: false });
            }
          }, { signal: this.signal });
        }
      });

      this.topNavList?.addEventListener('pointerleave', () => {
        if (!this.openItem) this.restoreIndicator();
      }, { signal: this.signal });

      this.topNavList?.addEventListener('scroll', () => {
        const target = this.hoveredLink || this.currentLink;
        if (target) this.moveIndicator(target, true);
      }, { passive: true, signal: this.signal });

      this.topNavList?.addEventListener('keydown', (event) => this.onTopNavKeydown(event), { signal: this.signal });
    }

    setupMegaMenuDelegation() {
      this.root.addEventListener('pointerover', (event) => {
        if (!desktopQuery.matches) return;
        const trigger = event.target.closest('[data-hz-menu-visual-trigger]');
        if (!trigger || !this.root.contains(trigger)) return;
        const related = event.relatedTarget;
        if (related && trigger.contains(related)) return;
        this.activateVisual(trigger);
      }, { signal: this.signal });

      this.root.addEventListener('focusin', (event) => {
        const trigger = event.target.closest('[data-hz-menu-visual-trigger]');
        if (trigger) this.activateVisual(trigger);
      }, { signal: this.signal });

      this.root.addEventListener('click', (event) => {
        const branch = event.target.closest('[data-hz-left-open]');
        if (branch) {
          event.preventDefault();
          this.openLeftPanel(branch);
          return;
        }

        const back = event.target.closest('[data-hz-left-back]');
        if (back) {
          event.preventDefault();
          this.backLeftPanel(back.closest('[data-hz-mega-menu]'));
        }
      }, { signal: this.signal });

      this.root.addEventListener('keydown', (event) => {
        const mega = event.target.closest('[data-hz-mega-menu]');
        if (!mega) return;
        this.onMegaKeydown(event, mega);
      }, { signal: this.signal });
    }

    setupMobileMenu() {
      this.mobileOpenButton?.addEventListener('click', () => this.openMobileMenu(this.mobileOpenButton), { signal: this.signal });
      this.mobileCloseButton?.addEventListener('click', () => this.closeMobileMenu(), { signal: this.signal });

      this.mobileMenu?.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-hz-mobile-open]');
        if (opener) {
          event.preventDefault();
          this.openMobilePanel(opener.dataset.hzMobileOpen, opener);
          return;
        }

        const back = event.target.closest('[data-hz-mobile-back]');
        if (back) {
          event.preventDefault();
          this.backMobilePanel();
          return;
        }

        const search = event.target.closest('[data-hz-mobile-search]');
        if (search) {
          event.preventDefault();
          this.closeMobileMenu({ restoreFocus: false, immediate: true });
          requestAnimationFrame(() => this.openSearch(search));
          return;
        }

      }, { signal: this.signal });

      this.mobileMenu?.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          if (this.mobileHistory.length > 1) this.backMobilePanel();
          else this.closeMobileMenu();
          return;
        }

        if (event.key === 'Tab' && this.root.classList.contains('hz__siteHeaderV2--mobileOpen')) {
          this.trapFocus(event, this.mobileMenu);
        }
      }, { signal: this.signal });
    }

    setupSearch() {
      if (!this.searchInput || !this.searchPanel) return;

      this.searchToggle?.addEventListener('click', () => {
        if (this.root.classList.contains('hz__siteHeaderV2--searchOpen')) this.closeSearch();
        else this.openSearch(this.searchToggle);
      }, { signal: this.signal });

      this.searchClose?.addEventListener('click', () => this.closeSearch(), { signal: this.signal });
      this.searchClear?.addEventListener('click', () => {
        this.searchInput.value = '';
        this.onSearchInput();
        this.searchInput.focus({ preventScroll: true });
      }, { signal: this.signal });

      this.searchInput.addEventListener('input', () => this.onSearchInput(), { signal: this.signal });
      this.searchInput.addEventListener('keydown', (event) => this.onSearchInputKeydown(event), { signal: this.signal });

      this.searchPanel.addEventListener('keydown', (event) => this.onSearchPanelKeydown(event), { signal: this.signal });

      this.searchForm?.addEventListener('submit', (event) => {
        const query = this.searchInput.value.trim();
        if (!query) {
          event.preventDefault();
          this.searchInput.focus({ preventScroll: true });
        }
      }, { signal: this.signal });
    }

    setupGlobalEvents() {
      this.backdrop?.addEventListener('click', () => this.closeAll(), { signal: this.signal });

      this.root.addEventListener('click', (event) => {
        const nativeAction = event.target.closest('[data-cart-sidebar], [data-open-auth-sidebar]');
        if (!nativeAction) return;

        this.closeMega(this.openItem, { immediate: true });
        this.closeSearch({ restoreFocus: false, immediate: true });
        this.closeMobileMenu({ restoreFocus: false, immediate: true });

        // Ella binds these controls only once during its own initialization. Keeping this
        // tiny class-level bridge makes a Theme Editor-reloaded header obey the same
        // native sidebar contract without duplicating any cart/account data logic.
        if (nativeAction.matches('[data-cart-sidebar]')) {
          event.preventDefault();
          if (document.body.classList.contains('template-cart')) {
            window.scrollTo({ top: 0, behavior: reducedMotionQuery.matches ? 'auto' : 'smooth' });
          } else {
            document.body.classList.add('cart-sidebar-show');
          }
        } else if (nativeAction.matches('[data-open-auth-sidebar]')) {
          event.preventDefault();
          if (document.body.classList.contains('template-customers-login')) {
            window.scrollTo({ top: 0, behavior: reducedMotionQuery.matches ? 'auto' : 'smooth' });
          } else {
            document.body.classList.add('auth-sidebar-show');
          }
        }
      }, { capture: true, signal: this.signal });

      document.addEventListener('keydown', (event) => {
        if (this.root.classList.contains('hz__siteHeaderV2--searchOpen') && event.key === 'Tab') {
          this.trapFocusAcross(event, [this.searchDock, this.searchPanel]);
          return;
        }
        if (event.key !== 'Escape') return;
        if (this.root.classList.contains('hz__siteHeaderV2--mobileOpen')) return;
        if (this.root.classList.contains('hz__siteHeaderV2--searchOpen')) {
          event.preventDefault();
          this.closeSearch();
          return;
        }
        if (this.openItem) {
          event.preventDefault();
          const link = this.openItem.querySelector(':scope > [data-hz-top-link]');
          this.closeMega(this.openItem);
          link?.focus({ preventScroll: true });
        }
      }, { signal: this.signal });

      window.addEventListener('resize', () => this.scheduleMeasure(), { passive: true, signal: this.signal });
      window.addEventListener('orientationchange', () => this.scheduleMeasure(), { passive: true, signal: this.signal });

      const queryListener = () => {
        this.closeMega(this.openItem, { immediate: true });
        if (desktopQuery.matches) this.closeMobileMenu({ restoreFocus: false, immediate: true });
        this.scheduleMeasure();
      };

      if (typeof desktopQuery.addEventListener === 'function') {
        desktopQuery.addEventListener('change', queryListener, { signal: this.signal });
      } else {
        desktopQuery.addListener(queryListener);
        this.signal.addEventListener('abort', () => desktopQuery.removeListener(queryListener), { once: true });
      }
    }

    setupSticky() {
      if (!this.sticky) return;
      window.addEventListener('scroll', () => {
        if (this.scrollRaf) return;
        this.scrollRaf = requestAnimationFrame(() => {
          this.scrollRaf = 0;
          this.onScroll();
        });
      }, { passive: true, signal: this.signal });
    }

    setupCartObserver() {
      this.root.querySelectorAll('[data-cart-count]').forEach((node) => {
        this.cartObserver.observe(node, { childList: true, characterData: true, subtree: true });
      });
    }

    onScroll() {
      const scrollY = Math.max(0, window.scrollY);
      const delta = scrollY - this.lastScrollY;
      const overlaysOpen = this.root.classList.contains('hz__siteHeaderV2--overlayOpen') ||
        document.body.classList.contains('cart-sidebar-show') ||
        document.body.classList.contains('auth-sidebar-show');

      this.root.classList.toggle('hz__siteHeaderV2--elevated', scrollY > 4);

      if (this.hideOnScroll && !overlaysOpen && scrollY > this.root.offsetHeight * 1.2) {
        if (delta > 5) this.root.classList.add('hz__siteHeaderV2--hidden');
        else if (delta < -5) this.root.classList.remove('hz__siteHeaderV2--hidden');
      } else {
        this.root.classList.remove('hz__siteHeaderV2--hidden');
      }

      this.lastScrollY = scrollY;
    }

    pointerOrFocusInsideItem(item) {
      if (!item) return false;
      const link = item.querySelector(':scope > [data-hz-top-link]');
      const mega = item.querySelector(':scope > [data-hz-mega-menu]');
      return Boolean(
        link?.matches(':hover') ||
        mega?.matches(':hover') ||
        item.contains(document.activeElement)
      );
    }

    prepareMega(item) {
      if (!item || !desktopQuery.matches) return;
      const mega = item.querySelector(':scope > [data-hz-mega-menu]');
      if (!mega) return;
      const firstVisual = mega.querySelector('[data-hz-left-panel="root"] [data-hz-menu-visual-trigger]');
      if (firstVisual) this.activateVisual(firstVisual);
    }

    scheduleOpen(item) {
      window.clearTimeout(this.closeTimer);
      window.clearTimeout(this.openTimer);
      this.prepareMega(item);
      this.openTimer = window.setTimeout(() => this.openMega(item), reducedMotionQuery.matches ? 0 : this.openDelay);
    }

    scheduleClose(item, delay = this.closeDelay) {
      if (!item) return;
      window.clearTimeout(this.openTimer);
      window.clearTimeout(this.closeTimer);
      this.closeTimer = window.setTimeout(() => {
        if (this.pointerOrFocusInsideItem(item)) return;
        this.closeMega(item);
      }, reducedMotionQuery.matches ? 0 : delay);
    }

    openMega(item, { focusFirst = false } = {}) {
      if (!item || !desktopQuery.matches) return;
      const link = item.querySelector(':scope > [data-hz-top-link]');
      const mega = item.querySelector(':scope > [data-hz-mega-menu]');
      if (!link || !mega) return;

      const pendingHide = this.megaHideTimers.get(mega);
      if (pendingHide) window.clearTimeout(pendingHide);
      this.megaHideTimers.delete(mega);
      mega.hidden = false;
      mega.inert = false;

      window.clearTimeout(this.openTimer);
      window.clearTimeout(this.closeTimer);
      this.closeSearch({ restoreFocus: false, immediate: true });
      this.closeMobileMenu({ restoreFocus: false, immediate: true });

      const previousItem = this.openItem && this.openItem !== item ? this.openItem : null;
      this.openItem = item;

      // Open the incoming menu before retiring the previous one. Because both
      // panels occupy the same header layer this prevents the backdrop/menu from
      // flashing between top-level destinations.
      mega.classList.add('hz__megaMenuV2--front');
      item.classList.add('hz__siteHeaderV2NavItem--open');
      link.setAttribute('aria-expanded', 'true');
      mega.setAttribute('aria-hidden', 'false');
      this.root.classList.add('hz__siteHeaderV2--overlayOpen');
      this.updateViewportBottom();
      this.moveIndicator(link, true);
      this.resetLeftPanels(mega);

      const firstVisual = mega.querySelector('[data-hz-left-panel="root"] [data-hz-menu-visual-trigger]');
      if (firstVisual) this.activateVisual(firstVisual);

      if (previousItem) {
        this.closeMega(previousItem, {
          restoreIndicator: false,
          immediate: true,
          preserveOverlay: true
        });
      }

      const frontDuration = reducedMotionQuery.matches ? 0 : 320;
      window.setTimeout(() => mega.classList.remove('hz__megaMenuV2--front'), frontDuration);
      if (focusFirst && firstVisual) firstVisual.focus({ preventScroll: true });
    }

    closeMega(item, { restoreIndicator = true, immediate = false, preserveOverlay = false } = {}) {
      if (!item) return;
      window.clearTimeout(this.openTimer);
      window.clearTimeout(this.closeTimer);

      const link = item.querySelector(':scope > [data-hz-top-link]');
      const mega = item.querySelector(':scope > [data-hz-mega-menu]');
      item.classList.remove('hz__siteHeaderV2NavItem--open');
      link?.setAttribute('aria-expanded', 'false');
      if (mega) {
        mega.setAttribute('aria-hidden', 'true');
        mega.inert = true;
        const pendingHide = this.megaHideTimers.get(mega);
        if (pendingHide) window.clearTimeout(pendingHide);
        const hideMega = () => {
          if (this.destroyed || item.classList.contains('hz__siteHeaderV2NavItem--open') || mega.getAttribute('aria-hidden') !== 'true') return;
          mega.hidden = true;
          this.megaHideTimers.delete(mega);
        };
        if (immediate || reducedMotionQuery.matches) {
          hideMega();
        } else {
          const hideTimer = window.setTimeout(hideMega, 310);
          this.megaHideTimers.set(mega, hideTimer);
        }
      }
      if (this.openItem === item) this.openItem = null;

      if (immediate && mega) {
        mega.style.transition = 'none';
        requestAnimationFrame(() => mega.style.removeProperty('transition'));
      }

      if (!preserveOverlay && !this.root.classList.contains('hz__siteHeaderV2--searchOpen') && !this.root.classList.contains('hz__siteHeaderV2--mobileOpen') && !this.openItem) {
        this.root.classList.remove('hz__siteHeaderV2--overlayOpen');
      }

      if (restoreIndicator) this.restoreIndicator();
    }

    closeAll() {
      this.closeMega(this.openItem, { immediate: false });
      this.closeSearch();
      this.closeMobileMenu();
    }

    moveIndicator(link, visible = true) {
      if (!this.indicator || !link || !desktopQuery.matches) return;
      const offsetParent = this.indicator.offsetParent || this.bar || this.root;
      if (!offsetParent) return;
      const navRect = offsetParent.getBoundingClientRect();
      const linkRect = link.getBoundingClientRect();
      const width = toNumber(getComputedStyle(this.root).getPropertyValue('--hz-headerIndicatorWidth'), 92);
      const x = linkRect.left - navRect.left + (linkRect.width - width) / 2;
      this.indicator.style.setProperty('--hz-headerIndicatorX', `${Math.round(x * 100) / 100}px`);
      this.indicator.classList.toggle('hz__siteHeaderV2Indicator--visible', Boolean(visible));
    }

    restoreIndicator() {
      this.hoveredLink = null;
      if (this.currentLink) this.moveIndicator(this.currentLink, true);
      else this.indicator?.classList.remove('hz__siteHeaderV2Indicator--visible');
    }

    resetLeftPanels(mega) {
      if (!mega) return;
      const panels = Array.from(mega.querySelectorAll('[data-hz-left-panel]'));
      panels.forEach((panel) => {
        const rootPanel = panel.dataset.hzLeftPanel === 'root';
        panel.hidden = !rootPanel;
        panel.classList.toggle('hz__megaMenuV2LeftPanel--active', rootPanel);
        panel.classList.remove('hz__megaMenuV2LeftPanel--previous');
        panel.setAttribute('aria-hidden', rootPanel ? 'false' : 'true');
        panel.inert = !rootPanel;
      });
      mega.querySelectorAll('[data-hz-left-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
      this.leftHistories.set(mega, ['root']);
    }

    openLeftPanel(button) {
      const mega = button.closest('[data-hz-mega-menu]');
      if (!mega) return;
      const targetKey = button.dataset.hzLeftOpen;
      const current = mega.querySelector('[data-hz-left-panel].hz__megaMenuV2LeftPanel--active');
      const target = Array.from(mega.querySelectorAll('[data-hz-left-panel]')).find((panel) => panel.dataset.hzLeftPanel === targetKey);
      if (!target || target === current) return;

      mega.querySelectorAll('[data-hz-left-open]').forEach((branch) => {
        branch.setAttribute('aria-expanded', branch === button ? 'true' : 'false');
      });
      const history = this.leftHistories.get(mega) || ['root'];
      if (history.at(-1) !== targetKey) history.push(targetKey);
      this.leftHistories.set(mega, history);
      this.transitionLeftPanels(current, target, 'forward');

      const firstVisual = target.querySelector('[data-hz-menu-visual-trigger]');
      if (firstVisual) this.activateVisual(firstVisual, { force: true });
    }

    backLeftPanel(mega) {
      if (!mega) return;
      const history = this.leftHistories.get(mega) || ['root'];
      if (history.length <= 1) return;
      const currentKey = history.pop();
      const previousKey = history.at(-1) || 'root';
      this.leftHistories.set(mega, history);
      const current = Array.from(mega.querySelectorAll('[data-hz-left-panel]')).find((panel) => panel.dataset.hzLeftPanel === currentKey);
      const previous = Array.from(mega.querySelectorAll('[data-hz-left-panel]')).find((panel) => panel.dataset.hzLeftPanel === previousKey);
      if (!current || !previous) return;
      mega.querySelectorAll('[data-hz-left-open]').forEach((branch) => {
        branch.setAttribute('aria-expanded', branch.dataset.hzLeftOpen === previousKey && previousKey !== 'root' ? 'true' : 'false');
      });
      this.transitionLeftPanels(current, previous, 'back');

      const activeVisual = previous.querySelector('.hz__megaMenuV2Link--active') || previous.querySelector('[data-hz-menu-visual-trigger]');
      if (activeVisual) this.activateVisual(activeVisual, { force: true });
    }

    transitionLeftPanels(current, target, direction) {
      const duration = reducedMotionQuery.matches ? 0 : 300;
      target.hidden = false;
      target.inert = false;
      target.setAttribute('aria-hidden', 'false');
      target.classList.remove('hz__megaMenuV2LeftPanel--previous');

      if (direction === 'back') {
        target.classList.add('hz__megaMenuV2LeftPanel--previous');
        requestAnimationFrame(() => {
          target.classList.remove('hz__megaMenuV2LeftPanel--previous');
          target.classList.add('hz__megaMenuV2LeftPanel--active');
          current.classList.remove('hz__megaMenuV2LeftPanel--active');
        });
      } else {
        current.classList.add('hz__megaMenuV2LeftPanel--previous');
        current.classList.remove('hz__megaMenuV2LeftPanel--active');
        requestAnimationFrame(() => target.classList.add('hz__megaMenuV2LeftPanel--active'));
      }

      current.inert = true;
      current.setAttribute('aria-hidden', 'true');
      window.setTimeout(() => {
        if (this.destroyed || current.classList.contains('hz__megaMenuV2LeftPanel--active')) return;
        current.hidden = true;
        current.classList.remove('hz__megaMenuV2LeftPanel--previous');
      }, duration + 30);
    }

    async activateVisual(trigger, { force = false } = {}) {
      if (!trigger) return;
      const mega = trigger.closest('[data-hz-mega-menu]');
      const visual = mega?.querySelector('[data-hz-menu-visual]');
      const content = mega?.querySelector('[data-hz-menu-visual-content]');
      const loader = mega?.querySelector('[data-hz-menu-loader]');
      if (!mega || !visual || !content) return;

      const key = trigger.dataset.hzVisualKey;
      const currentKey = visual.dataset.hzCurrentVisual || '';
      const requestedKey = visual.dataset.hzRequestedVisual || '';
      if (!key || (!force && (currentKey === key || requestedKey === key))) return;

      const template = Array.from(mega.querySelectorAll('template[data-hz-menu-template]')).find((node) => node.dataset.hzMenuTemplate === key);
      if (!template) return;

      mega.querySelectorAll('[data-hz-menu-visual-trigger].hz__megaMenuV2Link--active').forEach((node) => node.classList.remove('hz__megaMenuV2Link--active'));
      trigger.classList.add('hz__megaMenuV2Link--active');
      visual.dataset.hzRequestedVisual = key;

      const token = ++this.visualSwitchToken;
      content.querySelectorAll(':scope > .hz__megaMenuV2Staging').forEach((node) => node.remove());

      const staging = document.createElement('div');
      staging.className = 'hz__megaMenuV2Staging';
      staging.append(template.content.cloneNode(true));
      staging.querySelectorAll('img').forEach((img, index) => {
        img.loading = 'eager';
        if (index < 2) img.fetchPriority = 'high';
      });
      const hadContent = Array.from(content.children).some((node) => !node.classList.contains('hz__megaMenuV2Staging'));
      content.append(staging);

      // The first panel should establish the mega menu's natural height
      // immediately. Its media boxes already have deterministic dimensions, so
      // waiting for image decode here only creates a needless height pop on open.
      if (!hadContent) {
        const initialFragment = document.createDocumentFragment();
        while (staging.firstChild) initialFragment.append(staging.firstChild);
        content.replaceChildren(initialFragment);
        visual.dataset.hzCurrentVisual = key;
        visual.dataset.hzRequestedVisual = key;
        visual.setAttribute('aria-busy', 'false');
        if (loader) loader.hidden = true;
        return;
      }

      visual.setAttribute('aria-busy', 'true');
      let loaderTimer = window.setTimeout(() => {
        if (token === this.visualSwitchToken && loader) loader.hidden = false;
      }, reducedMotionQuery.matches ? 0 : 120);

      await Promise.race([this.waitForImages(staging), sleep(1000)]);
      window.clearTimeout(loaderTimer);
      if (this.destroyed || token !== this.visualSwitchToken) {
        staging.remove();
        return;
      }

      if (loader) loader.hidden = true;

      if (hadContent && !reducedMotionQuery.matches) {
        staging.classList.add('hz__megaMenuV2Staging--enter');
        await sleep(185);
        if (this.destroyed || token !== this.visualSwitchToken) {
          staging.remove();
          return;
        }
      }

      const fragment = document.createDocumentFragment();
      while (staging.firstChild) fragment.append(staging.firstChild);
      content.replaceChildren(fragment);
      visual.dataset.hzCurrentVisual = key;
      visual.dataset.hzRequestedVisual = key;
      visual.setAttribute('aria-busy', 'false');
    }

    waitForImages(container) {
      const images = Array.from(container.querySelectorAll('img'));
      if (!images.length) return Promise.resolve();
      return Promise.all(images.map((img) => {
        if (img.complete) {
          if (typeof img.decode === 'function') return img.decode().catch(() => undefined);
          return Promise.resolve();
        }
        return new Promise((resolve) => {
          const done = () => resolve();
          img.addEventListener('load', done, { once: true });
          img.addEventListener('error', done, { once: true });
        });
      }));
    }

    onTopNavKeydown(event) {
      const link = event.target.closest('[data-hz-top-link]');
      if (!link) return;
      const index = this.topLinks.indexOf(link);
      if (index < 0) return;

      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        const delta = event.key === 'ArrowRight' ? 1 : -1;
        const next = this.topLinks[(index + delta + this.topLinks.length) % this.topLinks.length];
        next?.focus({ preventScroll: true });
        next?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        return;
      }

      if (event.key === 'ArrowDown' && link.hasAttribute('data-hz-mega-trigger')) {
        event.preventDefault();
        const item = link.closest('[data-hz-top-item]');
        this.openMega(item, { focusFirst: true });
      }
    }

    onMegaKeydown(event, mega) {
      if (event.key === 'Escape') {
        event.preventDefault();
        const history = this.leftHistories.get(mega) || ['root'];
        if (history.length > 1) {
          this.backLeftPanel(mega);
        } else {
          const item = mega.closest('[data-hz-top-item]');
          const link = item?.querySelector(':scope > [data-hz-top-link]');
          this.closeMega(item);
          link?.focus({ preventScroll: true });
        }
        return;
      }

      const currentPanel = mega.querySelector('[data-hz-left-panel].hz__megaMenuV2LeftPanel--active');
      if (!currentPanel) return;
      const links = Array.from(currentPanel.querySelectorAll('[data-hz-menu-visual-trigger]'));
      const currentIndex = links.indexOf(document.activeElement);
      if (currentIndex < 0) return;

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        links[(currentIndex + delta + links.length) % links.length]?.focus({ preventScroll: true });
      }
    }

    openMobileMenu(opener) {
      if (!this.mobileMenu || desktopQuery.matches) return;
      this.closeSearch({ restoreFocus: false, immediate: true });
      this.closeMega(this.openItem, { immediate: true });
      this.mobileOpener = opener || this.mobileOpenButton;
      this.resetMobilePanels();
      this.root.classList.add('hz__siteHeaderV2--mobileOpen', 'hz__siteHeaderV2--overlayOpen');
      this.mobileMenu.setAttribute('aria-hidden', 'false');
      this.mobileOpenButton?.setAttribute('aria-expanded', 'true');
      this.updateViewportBottom();
      this.lockBody();
      requestAnimationFrame(() => this.mobileCloseButton?.focus({ preventScroll: true }));
    }

    closeMobileMenu({ restoreFocus = true, immediate = false } = {}) {
      if (!this.mobileMenu || !this.root.classList.contains('hz__siteHeaderV2--mobileOpen')) return;
      this.root.classList.remove('hz__siteHeaderV2--mobileOpen');
      this.mobileMenu.setAttribute('aria-hidden', 'true');
      this.mobileOpenButton?.setAttribute('aria-expanded', 'false');
      if (!this.root.classList.contains('hz__siteHeaderV2--searchOpen') && !this.openItem) this.root.classList.remove('hz__siteHeaderV2--overlayOpen');
      this.unlockBody();

      const finish = () => this.resetMobilePanels();
      if (immediate || reducedMotionQuery.matches) finish();
      else window.setTimeout(finish, this.mobilePanelDuration + 30);

      if (restoreFocus) this.mobileOpener?.focus?.({ preventScroll: true });
    }

    resetMobilePanels() {
      this.mobileHistory = ['root'];
      this.mobileMenu?.querySelectorAll('[data-hz-mobile-open]').forEach((opener) => opener.setAttribute('aria-expanded', 'false'));
      this.mobilePanels.forEach((panel) => {
        const isRoot = panel.dataset.hzMobilePanel === 'root';
        panel.hidden = !isRoot;
        panel.inert = !isRoot;
        panel.setAttribute('aria-hidden', isRoot ? 'false' : 'true');
        panel.classList.toggle('hz__mobileMenuV2Panel--active', isRoot);
        panel.classList.remove('hz__mobileMenuV2Panel--previous', 'hz__mobileMenuV2Panel--leavingRight');
        const scroller = panel.querySelector('.hz__mobileMenuV2PanelScroll');
        if (scroller) scroller.scrollTop = 0;
      });
    }

    openMobilePanel(key, opener) {
      if (!key) return;
      const currentKey = this.mobileHistory.at(-1) || 'root';
      if (key === currentKey) return;
      const current = this.mobilePanels.find((panel) => panel.dataset.hzMobilePanel === currentKey);
      const target = this.mobilePanels.find((panel) => panel.dataset.hzMobilePanel === key);
      if (!current || !target) return;

      opener?.setAttribute('aria-expanded', 'true');
      target.hidden = false;
      target.inert = false;
      target.setAttribute('aria-hidden', 'false');
      target.classList.remove('hz__mobileMenuV2Panel--previous', 'hz__mobileMenuV2Panel--leavingRight');
      current.classList.remove('hz__mobileMenuV2Panel--active');
      current.classList.add('hz__mobileMenuV2Panel--previous');
      current.inert = true;
      current.setAttribute('aria-hidden', 'true');
      this.mobileHistory.push(key);

      requestAnimationFrame(() => target.classList.add('hz__mobileMenuV2Panel--active'));
      requestAnimationFrame(() => target.querySelector('[data-hz-mobile-back]')?.focus({ preventScroll: true }));
    }

    backMobilePanel() {
      if (this.mobileHistory.length <= 1) {
        this.closeMobileMenu();
        return;
      }
      const currentKey = this.mobileHistory.pop();
      const previousKey = this.mobileHistory.at(-1) || 'root';
      const current = this.mobilePanels.find((panel) => panel.dataset.hzMobilePanel === currentKey);
      const previous = this.mobilePanels.find((panel) => panel.dataset.hzMobilePanel === previousKey);
      if (!current || !previous) return;

      previous.hidden = false;
      previous.inert = false;
      previous.setAttribute('aria-hidden', 'false');
      previous.classList.remove('hz__mobileMenuV2Panel--previous');
      previous.classList.add('hz__mobileMenuV2Panel--active');
      current.classList.remove('hz__mobileMenuV2Panel--active');
      current.classList.add('hz__mobileMenuV2Panel--leavingRight');
      current.inert = true;
      current.setAttribute('aria-hidden', 'true');

      const duration = reducedMotionQuery.matches ? 0 : this.mobilePanelDuration;
      window.setTimeout(() => {
        if (this.destroyed || current.classList.contains('hz__mobileMenuV2Panel--active')) return;
        current.hidden = true;
        current.classList.remove('hz__mobileMenuV2Panel--leavingRight');
      }, duration + 30);

      requestAnimationFrame(() => {
        const preferred = previous.querySelector(`[data-hz-mobile-open="${CSS.escape(currentKey)}"]`);
        preferred?.setAttribute('aria-expanded', 'false');
        (preferred || previous.querySelector('a, button'))?.focus({ preventScroll: true });
      });
    }

    openSearch(opener) {
      if (!this.searchPanel || !this.searchInput) return;
      this.closeMega(this.openItem, { restoreIndicator: true, immediate: true });
      this.closeMobileMenu({ restoreFocus: false, immediate: true });
      this.searchOpener = opener || this.searchToggle;
      this.root.classList.add('hz__siteHeaderV2--searchOpen', 'hz__siteHeaderV2--overlayOpen');
      this.searchDock?.setAttribute('aria-hidden', 'false');
      this.searchPanel.setAttribute('aria-hidden', 'false');
      this.searchToggle?.setAttribute('aria-expanded', 'true');
      this.searchInput.setAttribute('aria-expanded', 'true');
      this.updateViewportBottom();
      if (!desktopQuery.matches) this.lockBody();
      requestAnimationFrame(() => this.searchInput.focus({ preventScroll: true }));
      this.onSearchInput();
    }

    closeSearch({ restoreFocus = true, immediate = false } = {}) {
      if (!this.searchPanel || !this.root.classList.contains('hz__siteHeaderV2--searchOpen')) return;
      this.searchRequestSerial += 1;
      this.abortSearchRequest();
      window.clearTimeout(this.searchTimer);
      this.setSearchLoading(false);
      this.root.classList.remove('hz__siteHeaderV2--searchOpen');
      this.searchDock?.setAttribute('aria-hidden', 'true');
      this.searchPanel.setAttribute('aria-hidden', 'true');
      this.searchToggle?.setAttribute('aria-expanded', 'false');
      this.searchInput?.setAttribute('aria-expanded', 'false');
      if (!this.openItem && !this.root.classList.contains('hz__siteHeaderV2--mobileOpen')) this.root.classList.remove('hz__siteHeaderV2--overlayOpen');
      if (!desktopQuery.matches) this.unlockBody();

      if (immediate) {
        this.searchPanel.style.transition = 'none';
        requestAnimationFrame(() => this.searchPanel.style.removeProperty('transition'));
      }
      if (restoreFocus) this.searchOpener?.focus?.({ preventScroll: true });
    }

    setSearchLoading(isLoading) {
      if (this.searchLoader) this.searchLoader.hidden = !isLoading;
      this.searchPanel?.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    getFullSearchUrl(query) {
      const raw = this.searchUrl || '/search';
      let url;
      try {
        url = /^https?:\/\//i.test(raw) ? new URL(raw) : new URL(raw, window.location.href);
      } catch (_error) {
        try {
          url = new URL('/search', window.location.href);
        } catch (_fallbackError) {
          return `/search?q=${encodeURIComponent(query)}&options%5Bprefix%5D=last`;
        }
      }
      url.searchParams.set('q', query);
      url.searchParams.set('options[prefix]', 'last');
      return `${url.pathname}${url.search}${url.hash}`;
    }

    formatSearchMoney(value) {
      const amount = Number.parseFloat(value);
      if (!Number.isFinite(amount)) return '';
      const cents = Math.round(amount * 100);
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        try {
          return window.Shopify.formatMoney(cents, this.moneyFormat || window.money_format);
        } catch (_error) {
          // Fall through to Intl when a merchant/app money format is malformed.
        }
      }
      try {
        return new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: 'currency',
          currency: window.shop_currency || 'USD'
        }).format(amount);
      } catch (_error) {
        return amount.toFixed(2);
      }
    }

    sizedSearchImage(url, width) {
      if (!url) return '';
      try {
        const imageUrl = new URL(url, window.location.origin);
        imageUrl.searchParams.set('width', String(width));
        return imageUrl.toString();
      } catch (_error) {
        return url;
      }
    }

    createSearchIcon(type, className) {
      const ns = 'http://www.w3.org/2000/svg';
      const svg = document.createElementNS(ns, 'svg');
      svg.setAttribute('viewBox', '0 0 20 20');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');
      if (className) svg.setAttribute('class', className);

      if (type === 'search') {
        const circle = document.createElementNS(ns, 'circle');
        circle.setAttribute('cx', '8.3');
        circle.setAttribute('cy', '8.3');
        circle.setAttribute('r', '4.9');
        circle.setAttribute('fill', 'none');
        circle.setAttribute('stroke', 'currentColor');
        circle.setAttribute('stroke-width', '1.55');
        const path = document.createElementNS(ns, 'path');
        path.setAttribute('d', 'm12 12 4.1 4.1');
        path.setAttribute('fill', 'none');
        path.setAttribute('stroke', 'currentColor');
        path.setAttribute('stroke-linecap', 'round');
        path.setAttribute('stroke-width', '1.55');
        svg.append(circle, path);
      } else {
        const path = document.createElementNS(ns, 'path');
        path.setAttribute('d', 'M4.5 10h10M10.5 6l4 4-4 4');
        path.setAttribute('fill', 'none');
        path.setAttribute('stroke', 'currentColor');
        path.setAttribute('stroke-linecap', 'round');
        path.setAttribute('stroke-linejoin', 'round');
        path.setAttribute('stroke-width', '1.6');
        svg.append(path);
      }
      return svg;
    }

    appendHighlightedText(target, text, query) {
      const source = String(text || '');
      const needle = String(query || '').trim();
      if (!needle) {
        target.textContent = source;
        return;
      }
      const index = source.toLocaleLowerCase().indexOf(needle.toLocaleLowerCase());
      if (index < 0) {
        target.textContent = source;
        return;
      }
      if (index > 0) target.append(document.createTextNode(source.slice(0, index)));
      const mark = document.createElement('mark');
      mark.textContent = source.slice(index, index + needle.length);
      target.append(mark);
      if (index + needle.length < source.length) target.append(document.createTextNode(source.slice(index + needle.length)));
    }

    createSearchGroup(title, resources, kind, query) {
      if (!resources?.length) return null;
      const section = document.createElement('section');
      section.className = 'hz__predictiveSearchV2Group';

      const heading = document.createElement('p');
      heading.className = 'hz__predictiveSearchV2Eyebrow';
      heading.textContent = title;
      section.append(heading);

      const list = document.createElement('ul');
      list.className = 'hz__predictiveSearchV2List';
      list.setAttribute('role', 'list');

      resources.slice(0, this.searchSuggestionLimit).forEach((resource) => {
        const li = document.createElement('li');
        li.className = 'hz__predictiveSearchV2ListItem';
        const link = document.createElement('a');
        link.className = 'hz__predictiveSearchV2SuggestionLink';
        link.href = resource.url || this.getFullSearchUrl(resource.text || resource.title || query);
        link.dataset.hzSearchOption = '';

        const thumbUrl = kind === 'collection'
          ? resource.featured_image?.url
          : kind === 'article'
            ? (resource.image?.url || resource.image)
            : '';

        if (thumbUrl) {
          const media = document.createElement('span');
          media.className = 'hz__predictiveSearchV2SuggestionMedia';
          const img = document.createElement('img');
          img.className = 'hz__predictiveSearchV2SuggestionImage';
          img.src = this.sizedSearchImage(thumbUrl, 96);
          img.alt = '';
          img.loading = 'lazy';
          media.append(img);
          link.append(media);
        } else if (kind === 'query') {
          link.append(this.createSearchIcon('search', 'hz__predictiveSearchV2SmallIcon'));
        }

        const copy = document.createElement('span');
        copy.className = 'hz__predictiveSearchV2SuggestionCopy';
        const label = document.createElement('span');
        label.className = 'hz__predictiveSearchV2SuggestionLabel';
        this.appendHighlightedText(label, resource.text || resource.title || '', query);
        copy.append(label);

        if (kind !== 'query' && kind !== 'collection') {
          const meta = document.createElement('span');
          meta.className = 'hz__predictiveSearchV2SuggestionMeta';
          meta.textContent = kind === 'article' ? 'Article' : 'Page';
          copy.append(meta);
        }

        link.append(copy, this.createSearchIcon('arrow', 'hz__predictiveSearchV2Arrow'));
        li.append(link);
        list.append(li);
      });

      section.append(list);
      return section;
    }

    createSearchProduct(product, query, index) {
      const link = document.createElement('a');
      link.className = 'hz__predictiveSearchV2Product';
      link.href = product.url || '#';
      link.dataset.hzSearchOption = '';

      const media = document.createElement('span');
      media.className = 'hz__predictiveSearchV2ProductMedia';
      const imageUrl = product.featured_image?.url || product.image;
      if (imageUrl) {
        const img = document.createElement('img');
        img.className = 'hz__predictiveSearchV2ProductImage';
        img.src = this.sizedSearchImage(imageUrl, 360);
        img.srcset = [160, 240, 360, 480].map((width) => `${this.sizedSearchImage(imageUrl, width)} ${width}w`).join(', ');
        img.sizes = '(min-width: 1024px) 14vw, 42vw';
        img.alt = product.featured_image?.alt || product.title || '';
        img.loading = index < 2 ? 'eager' : 'lazy';
        if (index < 2) img.fetchPriority = 'high';
        media.append(img);
      } else {
        const placeholder = document.createElement('span');
        placeholder.className = 'hz__predictiveSearchV2ProductPlaceholder';
        placeholder.setAttribute('aria-hidden', 'true');
        placeholder.append(this.createSearchIcon('search'));
        media.append(placeholder);
      }

      if (product.available === false) {
        const soldOut = document.createElement('span');
        soldOut.className = 'hz__predictiveSearchV2SoldOut';
        soldOut.textContent = 'Sold out';
        media.append(soldOut);
      }

      const copy = document.createElement('span');
      copy.className = 'hz__predictiveSearchV2ProductCopy';

      const meta = document.createElement('span');
      meta.className = 'hz__predictiveSearchV2ProductMeta';
      meta.textContent = [product.vendor, product.type].filter(Boolean).join(' · ');
      if (meta.textContent) copy.append(meta);

      const title = document.createElement('span');
      title.className = 'hz__predictiveSearchV2ProductTitle';
      this.appendHighlightedText(title, product.title || '', query);
      copy.append(title);

      const priceRow = document.createElement('span');
      priceRow.className = 'hz__predictiveSearchV2ProductPriceRow';
      const min = Number.parseFloat(product.price_min ?? product.price);
      const max = Number.parseFloat(product.price_max ?? product.price);
      const compare = Number.parseFloat(product.compare_at_price_min);
      const price = document.createElement('span');
      price.className = 'hz__predictiveSearchV2ProductPrice';
      price.textContent = Number.isFinite(min)
        ? `${Number.isFinite(max) && max > min ? 'From ' : ''}${this.formatSearchMoney(min)}`
        : '';
      priceRow.append(price);

      if (Number.isFinite(compare) && Number.isFinite(min) && compare > min) {
        price.classList.add('hz__predictiveSearchV2ProductPrice--sale');
        const oldPrice = document.createElement('del');
        oldPrice.className = 'hz__predictiveSearchV2ProductCompare';
        oldPrice.textContent = this.formatSearchMoney(compare);
        priceRow.append(oldPrice);
      }
      copy.append(priceRow);
      link.append(media, copy);
      return link;
    }

    renderSearchResults(query, results = {}) {
      if (!this.searchResults) return;
      const queries = Array.isArray(results.queries) ? results.queries : [];
      const products = Array.isArray(results.products) ? results.products : [];
      const collections = this.searchShowCollections && Array.isArray(results.collections) ? results.collections : [];
      const pages = this.searchShowPages && Array.isArray(results.pages) ? results.pages : [];
      const articles = this.searchShowArticles && Array.isArray(results.articles) ? results.articles : [];
      const total = queries.length + products.length + collections.length + pages.length + articles.length;

      const root = document.createElement('div');
      root.className = 'hz__predictiveSearchV2';
      root.dataset.resultCount = String(total);

      const top = document.createElement('div');
      top.className = 'hz__predictiveSearchV2Topline';
      const summary = document.createElement('p');
      summary.className = 'hz__predictiveSearchV2Summary';
      summary.append(document.createTextNode('Results for '));
      const strong = document.createElement('strong');
      strong.textContent = `“${query}”`;
      summary.append(strong);
      const searchAll = document.createElement('a');
      searchAll.className = 'hz__predictiveSearchV2SearchAll';
      searchAll.href = this.getFullSearchUrl(query);
      searchAll.dataset.hzSearchOption = '';
      searchAll.append(document.createTextNode('View all results'), this.createSearchIcon('arrow', 'hz__predictiveSearchV2SearchAllIcon'));
      top.append(summary, searchAll);
      root.append(top);

      if (!total) {
        const empty = document.createElement('div');
        empty.className = 'hz__predictiveSearchV2Empty';
        empty.append(this.createSearchIcon('search', 'hz__predictiveSearchV2EmptyIcon'));
        const title = document.createElement('p');
        title.className = 'hz__predictiveSearchV2EmptyTitle';
        title.textContent = 'No matches yet';
        const text = document.createElement('p');
        text.className = 'hz__predictiveSearchV2EmptyText';
        text.textContent = 'Try a product name, category, model, or a shorter search term.';
        empty.append(title, text);
        root.append(empty);
        this.searchResults.replaceChildren(root);
        if (this.searchStatus) this.searchStatus.textContent = `No predictive results for ${query}.`;
        return;
      }

      const grid = document.createElement('div');
      grid.className = 'hz__predictiveSearchV2Grid';
      const suggestions = document.createElement('div');
      suggestions.className = 'hz__predictiveSearchV2Suggestions';
      [
        this.createSearchGroup('Suggestions', queries, 'query', query),
        this.createSearchGroup('Collections', collections, 'collection', query),
        this.createSearchGroup('Pages', pages, 'page', query),
        this.createSearchGroup('Stories', articles, 'article', query)
      ].filter(Boolean).forEach((group) => suggestions.append(group));

      const productSection = document.createElement('section');
      productSection.className = 'hz__predictiveSearchV2Products';
      const productsHeader = document.createElement('div');
      productsHeader.className = 'hz__predictiveSearchV2ProductsHeader';
      const productsTitle = document.createElement('p');
      productsTitle.className = 'hz__predictiveSearchV2Eyebrow';
      productsTitle.textContent = 'Products';
      productsHeader.append(productsTitle);
      productSection.append(productsHeader);

      if (products.length) {
        const productGrid = document.createElement('div');
        productGrid.className = 'hz__predictiveSearchV2ProductGrid';
        products.slice(0, this.searchProductLimit).forEach((product, index) => productGrid.append(this.createSearchProduct(product, query, index)));
        productSection.append(productGrid);
      } else {
        const noProducts = document.createElement('p');
        noProducts.className = 'hz__predictiveSearchV2NoProducts';
        noProducts.textContent = 'No matching products. Related suggestions are shown beside this panel.';
        productSection.append(noProducts);
      }

      grid.append(suggestions, productSection);
      root.append(grid);
      this.searchResults.replaceChildren(root);
      if (this.searchStatus) this.searchStatus.textContent = total === 1 ? '1 predictive search result available.' : `${total} predictive search results available.`;
    }

    renderSearchFallback(message = 'Predictive search is unavailable right now') {
      if (!this.searchResults) return;
      const fallback = document.createElement('div');
      fallback.className = 'hz__predictiveSearchV2Empty';
      fallback.append(this.createSearchIcon('search', 'hz__predictiveSearchV2EmptyIcon'));
      const title = document.createElement('p');
      title.className = 'hz__predictiveSearchV2EmptyTitle';
      title.textContent = message;
      const text = document.createElement('p');
      text.className = 'hz__predictiveSearchV2EmptyText';
      text.textContent = 'You can still search the complete store.';
      const link = document.createElement('a');
      link.className = 'hz__predictiveSearchV2FallbackLink';
      link.href = this.getFullSearchUrl(this.searchInput?.value.trim() || '');
      link.dataset.hzSearchOption = '';
      link.append(document.createTextNode('Search all'), this.createSearchIcon('arrow', 'hz__predictiveSearchV2SearchAllIcon'));
      fallback.append(title, text, link);
      this.searchResults.replaceChildren(fallback);
      if (this.searchStatus) this.searchStatus.textContent = `${message}. Full store search remains available.`;
    }

    onSearchInput() {
      if (!this.searchInput) return;
      const query = this.searchInput.value.trim().slice(0, 120);
      if (this.searchClear) this.searchClear.hidden = query.length === 0;
      this.searchInput.removeAttribute('aria-activedescendant');

      this.searchRequestSerial += 1;
      this.abortSearchRequest();
      window.clearTimeout(this.searchTimer);
      this.setSearchLoading(false);

      if (query.length < this.searchMinChars) {
        if (this.searchInitial) this.searchInitial.hidden = false;
        if (this.searchDynamic) this.searchDynamic.hidden = true;
        if (this.searchResults) this.searchResults.replaceChildren();
        if (this.searchStatus) {
          const remaining = this.searchMinChars - query.length;
          this.searchStatus.textContent = query.length ? `Type ${remaining} more character${remaining === 1 ? '' : 's'} for suggestions.` : '';
        }
        return;
      }

      if (this.searchInitial) this.searchInitial.hidden = true;
      if (this.searchDynamic) this.searchDynamic.hidden = false;
      if (this.searchResults) this.searchResults.replaceChildren();
      if (this.searchStatus) this.searchStatus.textContent = 'Loading search suggestions.';

      const cacheKey = query.toLocaleLowerCase();
      if (this.searchCache.has(cacheKey)) {
        this.renderSearchResults(query, this.searchCache.get(cacheKey));
        return;
      }

      const scheduledQuery = query;
      this.searchTimer = window.setTimeout(() => {
        if (this.searchInput?.value.trim().slice(0, 120) === scheduledQuery) this.fetchSearch(scheduledQuery);
      }, this.searchDebounce);
    }

    buildPredictiveUrl(query) {
      let url;
      try {
        url = /^https?:\/\//i.test(this.predictiveUrl)
          ? new URL(this.predictiveUrl)
          : new URL(this.predictiveUrl, window.location.href);
      } catch (_error) {
        try {
          url = new URL('/search/suggest', window.location.href);
        } catch (_fallbackError) {
          url = new URL('https://invalid.local/search/suggest');
        }
      }
      if (!url.pathname.endsWith('.json')) url.pathname = `${url.pathname.replace(/\/$/, '')}.json`;
      url.search = '';
      url.searchParams.set('q', query);
      const types = ['query', 'product'];
      if (this.searchShowCollections) types.push('collection');
      if (this.searchShowPages) types.push('page');
      if (this.searchShowArticles) types.push('article');
      url.searchParams.set('resources[type]', types.join(','));
      url.searchParams.set('resources[limit]', String(Math.max(this.searchProductLimit, this.searchSuggestionLimit)));
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][unavailable_products]', this.searchUnavailableProducts);
      return url;
    }

    async requestPredictiveSearch(query, signal, retry = 0) {
      const response = await fetch(this.buildPredictiveUrl(query).toString(), {
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        signal
      });

      if (response.status === 429 && retry < 1 && !signal.aborted) {
        const retryAfter = Math.min(1200, Math.max(120, toNumber(response.headers?.get?.('Retry-After'), 0.25) * 1000));
        await sleep(retryAfter);
        if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
        return this.requestPredictiveSearch(query, signal, retry + 1);
      }

      let payload = null;
      try {
        payload = await response.json();
      } catch (_error) {
        payload = null;
      }
      if (!response.ok) {
        const error = new Error(payload?.description || payload?.message || `Predictive search failed with ${response.status}`);
        error.status = response.status;
        throw error;
      }
      const results = payload?.resources?.results;
      if (!results || typeof results !== 'object') throw new Error('Predictive search returned an invalid response.');
      return results;
    }

    rememberSearch(query, results) {
      const key = query.toLocaleLowerCase();
      if (this.searchCache.has(key)) this.searchCache.delete(key);
      this.searchCache.set(key, results);
      while (this.searchCache.size > this.searchCacheMax) {
        this.searchCache.delete(this.searchCache.keys().next().value);
      }
    }

    async fetchSearch(query) {
      this.abortSearchRequest();
      const cacheKey = query.toLocaleLowerCase();
      if (this.searchCache.has(cacheKey)) {
        this.renderSearchResults(query, this.searchCache.get(cacheKey));
        return;
      }

      const requestSerial = ++this.searchRequestSerial;
      const controller = new AbortController();
      this.searchRequestController = controller;
      this.setSearchLoading(true);
      if (this.searchDynamic) this.searchDynamic.hidden = false;
      let timedOut = false;
      const timeoutId = window.setTimeout(() => {
        if (!controller.signal.aborted) {
          timedOut = true;
          controller.abort();
        }
      }, 6500);

      try {
        const results = await this.requestPredictiveSearch(query, controller.signal);
        if (requestSerial !== this.searchRequestSerial || this.searchInput?.value.trim().slice(0, 120) !== query) return;
        this.rememberSearch(query, results);
        this.renderSearchResults(query, results);
      } catch (error) {
        if (requestSerial !== this.searchRequestSerial) return;
        if (error?.name === 'AbortError' && !timedOut) return;
        const message = timedOut
          ? 'Search is taking longer than expected'
          : error?.status === 417
            ? 'Live suggestions are unavailable for this language'
            : 'Predictive search is temporarily unavailable';
        this.renderSearchFallback(message);
      } finally {
        window.clearTimeout(timeoutId);
        if (this.searchRequestController === controller) this.searchRequestController = null;
        if (requestSerial === this.searchRequestSerial) this.setSearchLoading(false);
      }
    }

    abortSearchRequest() {
      if (this.searchRequestController) {
        this.searchRequestController.abort();
        this.searchRequestController = null;
      }
    }


    onSearchInputKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeSearch();
        return;
      }
      if (event.key === 'ArrowDown') {
        const options = this.getSearchOptions();
        if (options.length) {
          event.preventDefault();
          options[0].focus({ preventScroll: true });
        }
      }
    }

    onSearchPanelKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeSearch();
        return;
      }
      const option = event.target.closest('[data-hz-search-option]');
      if (!option) return;
      const options = this.getSearchOptions();
      const index = options.indexOf(option);
      if (index < 0) return;

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        options[(index + delta + options.length) % options.length]?.focus({ preventScroll: true });
      } else if (event.key === 'Home') {
        event.preventDefault();
        options[0]?.focus({ preventScroll: true });
      } else if (event.key === 'End') {
        event.preventDefault();
        options.at(-1)?.focus({ preventScroll: true });
      }
    }

    getSearchOptions() {
      return Array.from(this.searchPanel?.querySelectorAll('[data-hz-search-option]') || []).filter((node) => !node.closest('[hidden]'));
    }

    trapFocus(event, container) {
      const focusables = Array.from(container.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'))
        .filter((node) => !node.closest('[hidden]') && !node.inert);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    trapFocusAcross(event, containers) {
      const focusables = containers
        .filter(Boolean)
        .flatMap((container) => Array.from(container.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')))
        .filter((node) => !node.closest('[hidden]') && !node.inert && node.getClientRects().length);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    lockBody() {
      if (this.scrollLocked) return;
      const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      document.body.style.setProperty('--hz-header-scrollbar-comp', `${scrollbarWidth}px`);
      document.body.classList.add('hz__headerV2ScrollLocked');
      this.scrollLocked = true;
    }

    unlockBody() {
      if (!this.scrollLocked) return;
      document.body.classList.remove('hz__headerV2ScrollLocked');
      document.body.style.removeProperty('--hz-header-scrollbar-comp');
      this.scrollLocked = false;
    }

    updateViewportBottom() {
      const rect = this.bar?.getBoundingClientRect() || this.root.getBoundingClientRect();
      this.root.style.setProperty('--hz-header-viewport-bottom', `${Math.max(0, rect.bottom)}px`);
    }

    syncCartCount() {
      const nodes = Array.from(this.root.querySelectorAll('[data-cart-count]'));
      let count = 0;
      nodes.forEach((node) => {
        const value = Number.parseInt(node.textContent.trim(), 10);
        if (Number.isFinite(value)) count = Math.max(count, value);
        node.classList.toggle('hz__siteHeaderV2CartCount--empty', !value && node.classList.contains('hz__siteHeaderV2CartCount'));
        node.classList.toggle('hz__mobileMenuV2CartCount--empty', !value && node.classList.contains('hz__mobileMenuV2CartCount'));
      });
      this.root.querySelectorAll('.hz__siteHeaderV2Action--cart, .hz__mobileMenuV2TopAction--cart').forEach((action) => {
        action.setAttribute('aria-label', `Cart, ${count} item${count === 1 ? '' : 's'}`);
      });
    }

    scheduleMeasure() {
      if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
      this.resizeRaf = requestAnimationFrame(() => {
        this.resizeRaf = 0;
        this.updateViewportBottom();
        if (desktopQuery.matches) this.restoreIndicator();
      });
    }

    selectBlock(blockId) {
      if (!blockId) return;
      const template = this.root.querySelector(`template[data-hz-block-id="${CSS.escape(blockId)}"]`);
      if (!template) return;
      const mega = template.closest('[data-hz-mega-menu]');
      const item = mega?.closest('[data-hz-top-item]');
      const key = template.dataset.hzMenuTemplate;
      if (!mega || !item || !key) return;

      this.openMega(item, { focusFirst: false });
      const trigger = Array.from(mega.querySelectorAll('[data-hz-menu-visual-trigger]')).find((node) => node.dataset.hzVisualKey === key);
      if (!trigger) return;

      const owningPanel = trigger.closest('[data-hz-left-panel]');
      if (owningPanel && owningPanel.dataset.hzLeftPanel !== 'root') {
        this.resetLeftPanels(mega);
        const current = mega.querySelector('[data-hz-left-panel="root"]');
        const target = owningPanel;
        target.hidden = false;
        const history = ['root', target.dataset.hzLeftPanel];
        this.leftHistories.set(mega, history);
        this.transitionLeftPanels(current, target, 'forward');
      }
      this.activateVisual(trigger, { force: true });
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      window.clearTimeout(this.openTimer);
      window.clearTimeout(this.closeTimer);
      window.clearTimeout(this.searchTimer);
      if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
      if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
      this.abortSearchRequest();
      this.cartObserver.disconnect();
      this.resizeObserver?.disconnect();
      this.abortController.abort();
      this.unlockBody();
      this.root.classList.remove(
        'hz__siteHeaderV2--ready',
        'hz__siteHeaderV2--overlayOpen',
        'hz__siteHeaderV2--mobileOpen',
        'hz__siteHeaderV2--searchOpen',
        'hz__siteHeaderV2--hidden',
        'hz__siteHeaderV2--elevated'
      );
    }
  }

  const init = (scope = document) => {
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));
    roots.forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new HzHeaderV2(root));
    });
  };

  const destroy = (scope) => {
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));
    roots.forEach((root) => {
      const instance = instances.get(root);
      if (!instance) return;
      instance.destroy();
      instances.delete(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const blockId = event.detail?.blockId;
    if (!blockId) return;
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      const instance = instances.get(root);
      if (instance) instance.selectBlock(blockId);
    });
  });
})();

/* ==========================================================================\n   AR Newsletter V2 + AR Footer V2\n   ========================================================================== */
(() => {
  const FOOTER_SELECTOR = '[data-hz-footer-v2]';
  const NEWSLETTER_SELECTOR = '[data-hz-newsletter-v2]';
  const mobileFooterQuery = window.matchMedia('(max-width: 767px)');
  const footerInstances = new WeakMap();
  const newsletterInstances = new WeakMap();

  class HzFooterV2 {
    constructor(root) {
      this.root = root;
      this.abortController = new AbortController();
      this.toggles = Array.from(root.querySelectorAll('[data-hz-footer-menu-toggle]'));
      this.onViewportChange = this.onViewportChange.bind(this);

      this.toggles.forEach((toggle) => {
        toggle.addEventListener('click', () => this.onToggle(toggle), { signal: this.abortController.signal });
      });

      if (mobileFooterQuery.addEventListener) {
        mobileFooterQuery.addEventListener('change', this.onViewportChange, { signal: this.abortController.signal });
      } else {
        mobileFooterQuery.addListener(this.onViewportChange);
      }

      this.root.classList.add('hz__footerV2--enhanced');
      this.syncForViewport();
    }

    getPanel(toggle) {
      const id = toggle.getAttribute('aria-controls');
      return id ? this.root.querySelector(`#${CSS.escape(id)}`) : null;
    }

    onToggle(toggle) {
      if (!mobileFooterQuery.matches) return;
      const panel = this.getPanel(toggle);
      if (!panel) return;
      const willOpen = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', String(willOpen));
      panel.hidden = !willOpen;
    }

    onViewportChange() {
      this.syncForViewport();
    }

    syncForViewport() {
      const isMobile = mobileFooterQuery.matches;
      this.toggles.forEach((toggle) => {
        const panel = this.getPanel(toggle);
        if (!panel) return;

        if (isMobile) {
          toggle.removeAttribute('tabindex');
          toggle.setAttribute('aria-expanded', 'false');
          panel.hidden = true;
        } else {
          toggle.setAttribute('tabindex', '-1');
          toggle.setAttribute('aria-expanded', 'true');
          panel.hidden = false;
        }
      });
    }

    destroy() {
      this.abortController.abort();
      if (!mobileFooterQuery.removeEventListener) mobileFooterQuery.removeListener(this.onViewportChange);
      this.root.classList.remove('hz__footerV2--enhanced');
      this.toggles.forEach((toggle) => {
        toggle.removeAttribute('tabindex');
        toggle.setAttribute('aria-expanded', 'true');
        const panel = this.getPanel(toggle);
        if (panel) panel.hidden = false;
      });
    }
  }

  class HzNewsletterV2 {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('.hz__newsletterV2Form');
      this.submit = root.querySelector('[data-hz-newsletter-submit]');
      this.abortController = new AbortController();
      this.onPageShow = this.onPageShow.bind(this);

      if (!this.form || !this.submit) return;

      this.form.addEventListener('submit', () => this.setLoading(true), { signal: this.abortController.signal });
      this.form.addEventListener('invalid', () => this.setLoading(false), {
        capture: true,
        signal: this.abortController.signal
      });
      window.addEventListener('pageshow', this.onPageShow, { signal: this.abortController.signal });
    }

    onPageShow() {
      this.setLoading(false);
    }

    setLoading(loading) {
      if (!this.submit) return;
      this.submit.classList.toggle('hz__newsletterV2Submit--loading', loading);
      this.submit.toggleAttribute('aria-busy', loading);
      this.submit.disabled = loading;
    }

    destroy() {
      this.setLoading(false);
      this.abortController.abort();
    }
  }

  const collectRoots = (scope, selector) => {
    const roots = [];
    if (scope?.matches?.(selector)) roots.push(scope);
    scope?.querySelectorAll?.(selector).forEach((root) => roots.push(root));
    return roots;
  };

  const init = (scope = document) => {
    collectRoots(scope, FOOTER_SELECTOR).forEach((root) => {
      if (!footerInstances.has(root)) footerInstances.set(root, new HzFooterV2(root));
    });
    collectRoots(scope, NEWSLETTER_SELECTOR).forEach((root) => {
      if (!newsletterInstances.has(root)) newsletterInstances.set(root, new HzNewsletterV2(root));
    });
  };

  const destroy = (scope) => {
    collectRoots(scope, FOOTER_SELECTOR).forEach((root) => {
      footerInstances.get(root)?.destroy();
      footerInstances.delete(root);
    });
    collectRoots(scope, NEWSLETTER_SELECTOR).forEach((root) => {
      newsletterInstances.get(root)?.destroy();
      newsletterInstances.delete(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
